/**
 * KPI Dashboard with Fixed Grid System
 * 
 * Architecture:
 * - Desktop: 100-column fixed grid (positions never change on resize)
 * - Mobile: 20-column fixed grid (positions never change on resize) 
 * - CSS scaling: Grid scales down/up on smaller screens but maintains exact positions
 * - Breakpoint switching: Auto-switches between desktop/mobile views at 768px
 * - View independence: Desktop and mobile layouts are completely separate
 * 
 * This prevents the layout repositioning issues that occurred with responsive grids.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Plus, X, Activity, FileBarChart, DollarSign, Edit3, Eye, Trash2, Calendar, ChevronDown, Type, CheckCircle, AlertTriangle, ShoppingCart, Package2, Monitor, Smartphone, Settings, Star } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
);

import PermissionGuard from '../components/PermissionGuard';
import { useBusiness } from '../contexts/BusinessContext';
import KPIDashboardSkeleton from '../components/KPIDashboardSkeleton';
import { 
  getKPIDashboards, 
  createKPIDashboard, 
  updateKPIDashboard,
  deleteKPIDashboard, 
  getDashboardReports,
  createDashboardReport,
  updateDashboardReportLayout,
  deleteDashboardReport,
  updateDashboardDefaultDateRange,
  configureOppositeView,
  markViewAsConfigured,
  detectDeviceType,
  KPIDashboard as DatabaseKPIDashboard,
  KPIDashboardReport as DatabaseKPIDashboardReport
} from '../lib/kpiDashboards';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import RGL, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { getSuppliers, Customer } from '../lib/customers';
import { createOrder, OrderItem as DBOrderItem, getPendingOrdersForItems } from '../lib/orders';
import { StockItem } from '../lib/stockItems';

const GridLayout = WidthProvider(RGL);

interface Dashboard {
  id: string;
  name: string;
  createdAt: Date;
  reports?: Report[];
  dateRange?: DateRangeOption;
  defaultDateRange?: DateRangeOption;
  createdDeviceType: 'desktop' | 'mobile';
  desktopConfigured: boolean;
  mobileConfigured: boolean;
}

interface Report {
  id: string;
  type: string;
  title: string;
  data?: any;
  layout: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
}

type DateRangeOption = 'this_month' | 'last_month' | 'next_month' | 'this_week' | 'last_week' | 'next_week' | 'this_year';

interface DateRange {
  startDate: Date;
  endDate: Date;
}

// Interfaces for Stock Alert functionality
interface OrderItem {
  item: StockItem;
  quantityToOrder: number;
  totalCost: number;
}

interface SupplierOrder {
  supplier: Customer;
  items: OrderItem[];
  totalCost: number;
  itemCount: number;
}

// StarRating component for Team Rating display
interface StarRatingProps {
  rating: number;
  size?: 'small' | 'medium' | 'large';
  showValue?: boolean;
  dynamicSize?: number; // Custom size in pixels for dynamic scaling
}

const StarRating: React.FC<StarRatingProps> = ({ rating, size = 'medium', showValue = false, dynamicSize }) => {
  const sizeClasses = {
    small: 'h-4 w-4',
    medium: 'h-5 w-5',
    large: 'h-6 w-6'
  };

  // Calculate dynamic star size based on font size if provided
  const starSize = dynamicSize ? `${Math.max(dynamicSize * 0.6, 12)}px` : undefined;

  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  return (
    <div className="flex items-center space-x-1">
      <div className="flex space-x-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${dynamicSize ? '' : sizeClasses[size]} ${
              star <= fullStars
                ? 'text-yellow-400 fill-current'
                : star === fullStars + 1 && hasHalfStar
                ? 'text-yellow-400 fill-current opacity-50'
                : 'text-gray-300'
            }`}
            style={dynamicSize ? { width: starSize, height: starSize } : undefined}
          />
        ))}
      </div>
      {showValue && (
        <span className="text-sm font-medium text-gray-700 ml-2">{rating.toFixed(1)}</span>
      )}
    </div>
  );
};

// Utility functions for date range calculations
const getDateRange = (option: DateRangeOption): DateRange => {
  const now = new Date();
  const startOfWeek = (date: Date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day;
    return new Date(start.setDate(diff));
  };
  
  const endOfWeek = (date: Date) => {
    const end = new Date(startOfWeek(date));
    end.setDate(end.getDate() + 6);
    return end;
  };

  const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
  const endOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

  switch (option) {
    case 'this_month':
      return {
        startDate: startOfMonth(now),
        endDate: endOfMonth(now)
      };
    case 'last_month':
      return {
        startDate: startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1)),
        endDate: endOfMonth(new Date(now.getFullYear(), now.getMonth() - 1))
      };
    case 'next_month':
      return {
        startDate: startOfMonth(new Date(now.getFullYear(), now.getMonth() + 1)),
        endDate: endOfMonth(new Date(now.getFullYear(), now.getMonth() + 1))
      };
    case 'this_week':
      return {
        startDate: startOfWeek(now),
        endDate: endOfWeek(now)
      };
    case 'last_week':
      const lastWeek = new Date(now);
      lastWeek.setDate(now.getDate() - 7);
      return {
        startDate: startOfWeek(lastWeek),
        endDate: endOfWeek(lastWeek)
      };
    case 'next_week':
      const nextWeek = new Date(now);
      nextWeek.setDate(now.getDate() + 7);
      return {
        startDate: startOfWeek(nextWeek),
        endDate: endOfWeek(nextWeek)
      };
    case 'this_year':
      return {
        startDate: new Date(now.getFullYear(), 0, 1), // January 1st of current year
        endDate: new Date(now.getFullYear(), 11, 31) // December 31st of current year
      };
    default:
      return {
        startDate: startOfMonth(now),
        endDate: endOfMonth(now)
      };
  }
};

const getDateRangeLabel = (option: DateRangeOption): string => {
  const labels: Record<DateRangeOption, string> = {
    'this_month': 'This Month',
    'last_month': 'Last Month', 
    'next_month': 'Next Month',
    'this_week': 'This Week',
    'last_week': 'Last Week',
    'next_week': 'Next Week',
    'this_year': 'This Year'
  };
  return labels[option];
};

// Date Range Selector Component
interface DateRangeSelectorProps {
  value: DateRangeOption;
  onChange: (value: DateRangeOption) => void;
  defaultValue?: DateRangeOption;
  onSetDefault?: (value: DateRangeOption) => void;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ value, onChange, defaultValue, onSetDefault }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const options: DateRangeOption[] = [
    'this_month',
    'last_month', 
    'next_month',
    'this_week',
    'last_week',
    'next_week',
    'this_year'
  ];

  const handleSelect = (option: DateRangeOption) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleSetDefault = (option: DateRangeOption, event: React.MouseEvent) => {
    event.stopPropagation();
    if (onSetDefault) {
      onSetDefault(option);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isOpen && !target.closest('.date-range-selector')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const isMobile = window.innerWidth < 768;

  return (
    <div className="relative date-range-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`${isMobile ? 'px-2 py-1.5 text-xs min-h-[36px]' : 'px-3 py-2 text-sm'} font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 flex items-center ${isMobile ? 'space-x-1' : 'space-x-2'} transition-all duration-200`}
      >
        <Calendar className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
        <span className={isMobile ? 'truncate max-w-[80px]' : ''}>{getDateRangeLabel(value)}</span>
        <ChevronDown className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full mt-1 left-0 bg-white border border-blue-200 rounded-lg shadow-lg z-50 min-w-[200px]">
          <div className="px-3 py-2 text-xs font-medium text-blue-600 border-b border-blue-200 bg-blue-50">
            Select Date Range
          </div>
          {options.map((option) => (
            <div
              key={option}
              className={`flex items-center justify-between px-3 py-2 text-sm hover:bg-blue-50 ${
                value === option ? 'bg-blue-100' : ''
              }`}
            >
              <button
                onClick={() => handleSelect(option)}
                className={`flex-1 text-left ${
                  value === option ? 'text-blue-700 font-medium' : 'text-gray-700'
                }`}
              >
                {getDateRangeLabel(option)}
              </button>
              {onSetDefault && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={defaultValue === option}
                    onChange={(e) => e.stopPropagation()}
                    onClick={(e) => handleSetDefault(option, e)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-500">Default</span>
                </div>
              )}
            </div>
          ))}
          {onSetDefault && (
            <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-200 bg-gray-50">
              Check a box to set as default for this dashboard
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to format currency without unnecessary decimals
const formatCurrency = (amount: number, showSign: boolean = false): string => {
  const sign = showSign && amount >= 0 ? '+' : '';
  const absAmount = Math.abs(amount);
  
  // If the amount has no decimal part, don't show decimals
  if (absAmount % 1 === 0) {
    return `${sign}$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  } else {
    return `${sign}$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

function KPIDashboard() {
  const { businessId } = useBusiness();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dashboardName, setDashboardName] = useState('');
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddReportsModalOpen, setIsAddReportsModalOpen] = useState(false);
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [reportTitleTexts, setReportTitleTexts] = useState<{[key: string]: string}>({});
  
  // Pending changes state for edit mode
  const [pendingDashboardTitles, setPendingDashboardTitles] = useState<{[key: string]: string}>({});
  const [pendingReportTitles, setPendingReportTitles] = useState<{[key: string]: string}>({});
  const [pendingLayouts, setPendingLayouts] = useState<{[key: string]: any[]}>({});
  const [originalState, setOriginalState] = useState<{
    dashboardTitles: {[key: string]: string};
    reportTitles: {[key: string]: string};
    layouts: {[key: string]: any[]};
  }>({ dashboardTitles: {}, reportTitles: {}, layouts: {} });
  
  // Collision detection state
  const [overlappingReports, setOverlappingReports] = useState<Set<string>>(new Set());
  
  // Stock Alert specific state
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [supplierOrders, setSupplierOrders] = useState<SupplierOrder[]>([]);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [pendingOrderItems, setPendingOrderItems] = useState<Map<string, number>>(new Map());
  
  // Device-specific view state
  const [currentViewType, setCurrentViewType] = useState<'desktop' | 'mobile'>(() => detectDeviceType());
  const [configuringView, setConfiguringView] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  
  // Add state for tracking screen size for automatic view switching
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Calculate minimum grid height based on reports and default canvas size
  const getGridHeight = useCallback((dashboard: Dashboard) => {
    const defaultHeight = currentViewType === 'desktop' ? 30 : 40; // Default canvas: Desktop 100x30, Mobile 20x40
    const rowHeight = 8; // Consistent 8px row height for both mobile and desktop
    
    if (!dashboard.reports || dashboard.reports.length === 0) {
      return defaultHeight * rowHeight; // Return default canvas size when empty
    }
    
    // Find the maximum Y + height position from all reports
    const maxBottomPosition = Math.max(
      defaultHeight, // Never go smaller than default canvas
      ...dashboard.reports.map(report => report.layout.y + report.layout.h)
    );
    
    return maxBottomPosition * rowHeight;
  }, [currentViewType]);

  // Add breakpoint detection for automatic view switching
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setScreenWidth(newWidth);
      
      // Smart auto-switching: Only switch when both views exist and screen size changes
      const shouldBeMobile = newWidth < 768; // Switch to mobile view at 768px and below
      
      if (shouldBeMobile && currentViewType === 'desktop') {
        // Only switch if there are dashboards configured for both views
        const hasIndependentViews = dashboards.some(d => d.desktopConfigured && d.mobileConfigured);
        const hasMobileOnlyViews = dashboards.some(d => d.mobileConfigured && !d.desktopConfigured);
        
        if (hasIndependentViews || hasMobileOnlyViews) {
          setCurrentViewType('mobile');
        }
      } else if (!shouldBeMobile && currentViewType === 'mobile') {
        // Only switch if there are dashboards configured for both views
        const hasIndependentViews = dashboards.some(d => d.desktopConfigured && d.mobileConfigured);
        const hasDesktopOnlyViews = dashboards.some(d => d.desktopConfigured && !d.mobileConfigured);
        
        if (hasIndependentViews || hasDesktopOnlyViews) {
          setCurrentViewType('desktop');
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentViewType, dashboards]);
  
  // Refs for debouncing auto-save
  const dashboardTitleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reportTitleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load dashboards from database
  useEffect(() => {
    if (businessId) {
      loadDashboards();
    }
  }, [businessId]);

  // Reload dashboards when view type changes
  useEffect(() => {
    if (businessId) {
      setIsInitialLoadComplete(false); // Reset initial load state
      loadDashboards();
    }
  }, [currentViewType]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (dashboardTitleTimeoutRef.current) {
        clearTimeout(dashboardTitleTimeoutRef.current);
      }
      if (reportTitleTimeoutRef.current) {
        clearTimeout(reportTitleTimeoutRef.current);
      }
    };
  }, []);

  const loadDashboards = async (viewType?: 'desktop' | 'mobile') => {
    if (!businessId) return;
    
    try {
      setLoading(true);
      
      // Pre-load all data in parallel for faster loading
      const dashboardsData = await getKPIDashboards(businessId);
      
      // Load reports for each dashboard in parallel
      const dashboardsWithReports: Dashboard[] = await Promise.all(
        dashboardsData.map(async (d) => {
          const reportsData = await getDashboardReports(d.id, viewType || currentViewType);
          const defaultDateRange = (d.default_date_range as DateRangeOption) || 'this_month';
        
          // Update report data with real data based on the default date range
          const reports: Report[] = await Promise.all(
            reportsData.map(async (r) => ({
              id: r.id,
              type: r.report_type,
              title: r.report_title,
              data: r.report_type === 'title' 
                ? r.report_data // Preserve saved title data
                : await getFilteredReportDataWithComparison(r.report_type, defaultDateRange, businessId),
              layout: {
                x: r.layout_x,
                y: r.layout_y,
                w: r.layout_w,
                h: r.layout_h
              }
            }))
          );
          
          return {
            id: d.id,
            name: d.title,
            createdAt: new Date(d.created_at),
            reports,
            dateRange: defaultDateRange, // Use the saved default date range
            defaultDateRange: defaultDateRange,
            createdDeviceType: d.created_device_type,
            desktopConfigured: d.desktop_configured,
            mobileConfigured: d.mobile_configured
          };
        })
      );
      
      // Set all data at once to prevent partial renders
      setDashboards(dashboardsWithReports);
      
      // Initialize reportTitleTexts state for all title elements
      const titleTexts: {[key: string]: string} = {};
      dashboardsWithReports.forEach(dashboard => {
        dashboard.reports?.forEach(report => {
          if (report.type === 'title' && report.data?.text) {
            titleTexts[report.id] = report.data.text;
          }
        });
      });
      setReportTitleTexts(titleTexts);
      
      // Small delay to ensure smooth rendering
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Mark initial load as complete
      setIsInitialLoadComplete(true);
      
    } catch (error) {
      console.error('Error loading dashboards:', error);
      toast.error('Failed to load dashboards');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDashboard = async () => {
    if (!businessId) {
      toast.error('Business not selected');
      return;
    }

    if (dashboardName.trim()) {
      try {
        // Pass current view type to ensure dashboard is only created for current view
        const newDashboard = await createKPIDashboard({
          business_id: businessId,
          title: dashboardName.trim()
        }, currentViewType);
        
        const formattedDashboard: Dashboard = {
          id: newDashboard.id,
          name: newDashboard.title,
          createdAt: new Date(newDashboard.created_at),
          dateRange: 'this_month', // Default to this month
          defaultDateRange: 'this_month', // Default to this month
          createdDeviceType: newDashboard.created_device_type,
          desktopConfigured: newDashboard.desktop_configured,
          mobileConfigured: newDashboard.mobile_configured
        };
        
        setDashboards(prev => [...prev, formattedDashboard]);
      setDashboardName('');
      setIsModalOpen(false);
        toast.success(`Dashboard created for ${currentViewType} view only`);
      } catch (error) {
        console.error('Error creating dashboard:', error);
        toast.error('Failed to create dashboard');
      }
    }
  };

  const handleDateRangeChange = async (dashboardId: string, newDateRange: DateRangeOption) => {
    if (!businessId) return;
    
    setDashboards(prev => prev.map(dashboard => {
      if (dashboard.id === dashboardId) {
        return {
          ...dashboard,
          dateRange: newDateRange
        };
      }
      return dashboard;
    }));

    // Update reports with new filtered data
    const dashboard = dashboards.find(d => d.id === dashboardId);
    if (dashboard?.reports) {
      const updatedReports = await Promise.all(
        dashboard.reports.map(async (report) => ({
          ...report,
          data: report.type === 'title' 
            ? report.data // Preserve title data when changing date range
            : report.type === 'stock_alert'
            ? report.data // Preserve stock alert data - it's live and date-independent
            : await getFilteredReportDataWithComparison(report.type, newDateRange, businessId)
        }))
      );

      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            reports: updatedReports
          };
        }
        return dashboard;
      }));
    }
    
    toast.success(`Date range updated to ${getDateRangeLabel(newDateRange)}`);
  };

  const handleSetDefaultDateRange = async (dashboardId: string, newDefaultDateRange: DateRangeOption) => {
    try {
      await updateDashboardDefaultDateRange(dashboardId, newDefaultDateRange);
      
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            defaultDateRange: newDefaultDateRange
          };
        }
        return dashboard;
      }));
      
      toast.success(`Default date range set to ${getDateRangeLabel(newDefaultDateRange)}`);
    } catch (error) {
      console.error('Error setting default date range:', error);
      toast.error('Failed to save default date range');
    }
  };

  // Function to fetch real sales data from the database
  const fetchSalesData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query non-deleted bookings within the date range, excluding cancelled bookings
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0])
        .neq('status', 'cancelled');

      if (error) {
        console.error('Error fetching sales data:', error);
        throw error;
      }

      // Calculate total sales after discounts (same logic as Sales.tsx)
      const totalAfterDiscounts = (bookings || []).reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0);

      return {
        value: formatCurrency(totalAfterDiscounts, true),
        label: `Total Sales After Discounts`
      };
    } catch (error) {
      console.error('Error fetching sales data:', error);
      // Return fallback data on error
      return {
        value: '+$0',
        label: `Total Sales After Discounts`
      };
    }
  };

  // Function to fetch real phone calls data from call_history table
  const fetchPhoneCallsData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query call_history for inbound calls within the date range
      const { data: calls, error } = await supabase
        .from('call_history')
        .select('*')
        .eq('business_id', businessId)
        .eq('direction', 'inbound')
        .gte('call_time', startDate.toISOString())
        .lte('call_time', endDate.toISOString());

      if (error) {
        console.error('Error fetching phone calls data:', error);
        return { value: '0', label: 'Incoming Phone Calls' };
      }

      const phoneCallsCount = calls?.length || 0;
      
      return {
        value: phoneCallsCount.toString(),
        label: 'Incoming Phone Calls'
      };
    } catch (error) {
      console.error('Error in fetchPhoneCallsData:', error);
      return { value: '0', label: 'Incoming Phone Calls' };
    }
  };

  // Function to fetch real missed calls data from call_history table
  const fetchMissedCallsData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query call_history for inbound missed calls within the date range
      const { data: calls, error } = await supabase
        .from('call_history')
        .select('*')
        .eq('business_id', businessId)
        .eq('direction', 'inbound')
        .eq('status', 'missed')
        .gte('call_time', startDate.toISOString())
        .lte('call_time', endDate.toISOString());

      if (error) {
        console.error('Error fetching missed calls data:', error);
        return { value: '0', label: 'Missed Calls' };
      }

      const missedCallsCount = calls?.length || 0;
      
      return {
        value: missedCallsCount.toString(),
        label: 'Missed Calls'
      };
    } catch (error) {
      console.error('Error in fetchMissedCallsData:', error);
      return { value: '0', label: 'Missed Calls' };
    }
  };

  // Function to fetch real upsells data from the database
  const fetchUpsellsData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query non-deleted bookings within the date range, excluding cancelled bookings, only those with upsells data
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('upsells, status, end_date')
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0])
        .neq('status', 'cancelled')
        .not('upsells', 'is', null);

      if (error) {
        console.error('Error fetching upsells data:', error);
        throw error;
      }

      // Calculate total upsells (same logic as Sales.tsx)
      const totalUpsells = (bookings || []).reduce((sum, booking) => {
        return sum + (booking.upsells || 0);
      }, 0);

      return {
        value: formatCurrency(totalUpsells, true),
        label: `Total Upsells`
      };
    } catch (error) {
      console.error('Error fetching upsells data:', error);
      // Return fallback data on error
      return {
        value: '+$0',
        label: `Total Upsells`
      };
    }
  };

  // Function to fetch real website visits data from analytics_events table
  const fetchWebsiteVisitsData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query analytics_events for page_view events within the date range
      const { data: events, error } = await supabase
        .from('analytics_events')
        .select('id, event_type, timestamp, session_id')
        .eq('business_id', businessId)
        .eq('event_type', 'page_view')
        .gte('timestamp', startDate.toISOString())
        .lte('timestamp', endDate.toISOString());

      if (error) {
        console.error('Error fetching website visits data:', error);
        throw error;
      }

      // Count unique sessions (not total page views)
      const uniqueSessions = new Set(events?.map(event => event.session_id) || []);
      const totalVisits = uniqueSessions.size;

      return {
        value: totalVisits.toLocaleString(),
        label: `Website Visits`
      };
    } catch (error) {
      console.error('Error fetching website visits data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Website Visits`
      };
    }
  };

  // Function to fetch real Google reviews data from analytics_events table
  const fetchGoogleReviewsData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query analytics_events for google_review_clicked events within the date range
      const { data: events, error } = await supabase
        .from('analytics_events')
        .select('id, event_type, timestamp')
        .eq('business_id', businessId)
        .eq('event_type', 'google_review_clicked')
        .gte('timestamp', startDate.toISOString())
        .lte('timestamp', endDate.toISOString());

      if (error) {
        console.error('Error fetching Google reviews data:', error);
        throw error;
      }

      // Count total Google review clicks
      const totalReviews = events?.length || 0;

      return {
        value: totalReviews.toLocaleString(),
        label: `Google Reviews`
      };
    } catch (error) {
      console.error('Error fetching Google reviews data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Google Reviews`
      };
    }
  };

  // Function to fetch real closed sales data from the database
  const fetchClosedSalesData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query only completed non-deleted bookings within the date range (same logic as ProfitLoss.tsx)
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('status', 'completed') // Only completed bookings for closed sales
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (error) {
        console.error('Error fetching closed sales data:', error);
        throw error;
      }

      // Calculate total closed sales after discounts (same logic as ProfitLoss.tsx)
      const totalClosedSales = (bookings || []).reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0);

      return {
        value: formatCurrency(totalClosedSales, true),
        label: `Closed Sales`
      };
    } catch (error) {
      console.error('Error fetching closed sales data:', error);
      // Return fallback data on error
      return {
        value: '+$0',
        label: `Closed Sales`
      };
    }
  };

  // Function to fetch real supplies purchased data from the database
  const fetchSuppliesPurchasedData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query stock transactions for purchases within the date range
      // Need to join with stock_items to filter by business_id since the view doesn't include it directly
      const { data: transactions, error } = await supabase
        .from('stock_transactions')
        .select(`
          quantity,
          price,
          created_at,
          type,
          stock_items!inner(business_id)
        `)
        .eq('type', 'purchase') // Only purchase transactions
        .eq('stock_items.business_id', businessId) // Filter by business
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching supplies purchased data:', error);
        throw error;
      }

      // Calculate total supplies purchased cost (same logic as SuppliesUsage.tsx)
      const totalSuppliesPurchased = (transactions || []).reduce((sum, transaction) => {
        const quantity = transaction.quantity || 0;
        const price = transaction.price || 0;
        const cost = quantity * price;
        return sum + cost;
      }, 0);

      return {
        value: formatCurrency(totalSuppliesPurchased),
        label: `Supplies Purchased`
      };
    } catch (error) {
      console.error('Error fetching supplies purchased data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Supplies Purchased`
      };
    }
  };

  // Helper function for precise date filtering (matches ProfitLoss.tsx)
  const isDateInRange = (date: string | null | undefined, rangeOption: DateRangeOption): boolean => {
    if (!date) return false;
    
    // Convert the booking end date to UTC midnight
    const targetDate = new Date(date + 'T00:00:00Z');
    const now = new Date();
    const utcNow = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));

    switch (rangeOption) {
      case 'this_month': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 1));
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 0, 23, 59, 59, 999));
        return targetDate >= start && targetDate <= end;
      }
      case 'last_month': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1));
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 0, 23, 59, 59, 999));
        return targetDate >= start && targetDate <= end;
      }
      case 'next_month': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 1));
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 2, 0, 23, 59, 59, 999));
        return targetDate >= start && targetDate <= end;
      }
      case 'this_week': {
        const startOfWeek = (date: Date) => {
          const d = new Date(date);
          const day = d.getUTCDay();
          const diff = d.getUTCDate() - day;
          return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), diff));
        };
        const start = startOfWeek(utcNow);
        const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000 + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);
        return targetDate >= start && targetDate <= end;
      }
      case 'last_week': {
        const startOfWeek = (date: Date) => {
          const d = new Date(date);
          const day = d.getUTCDay();
          const diff = d.getUTCDate() - day - 7;
          return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), diff));
        };
        const start = startOfWeek(utcNow);
        const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000 + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);
        return targetDate >= start && targetDate <= end;
      }
      case 'next_week': {
        const startOfWeek = (date: Date) => {
          const d = new Date(date);
          const day = d.getUTCDay();
          const diff = d.getUTCDate() - day + 7;
          return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), diff));
        };
        const start = startOfWeek(utcNow);
        const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000 + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);
        return targetDate >= start && targetDate <= end;
      }
      case 'this_year': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), 0, 1)); // January 1st
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), 11, 31, 23, 59, 59, 999)); // December 31st
        return targetDate >= start && targetDate <= end;
      }
      default:
        return false;
    }
  };

  // Function to fetch real job materials data from the database (matches ProfitLoss.tsx calculation)
  const fetchJobMaterialsData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Get job materials costs from booking_materials table (same as ProfitLoss.tsx)
      const { data: materialsData, error: materialsError } = await supabase
        .from('booking_materials')
        .select(`
          id,
          quantity,
          cost,
          total_cost,
          created_at,
          material:material_id (
            id,
            name,
            description,
            unit,
            category:category_id (
              id,
              name
            )
          ),
          booking:booking_id (
            id,
            status,
            end_date,
            business_id
          )
        `)
        .eq('booking.business_id', businessId)
        .eq('booking.status', 'completed')
        .gte('booking.end_date', startDate.toISOString().split('T')[0])
        .lte('booking.end_date', endDate.toISOString().split('T')[0]);

      if (materialsError) {
        console.error('Error fetching materials for job materials:', materialsError);
        throw materialsError;
      }

      // Apply additional date filtering (same as ProfitLoss.tsx)
      const filteredMaterials = (materialsData || []).filter(material => {
        return material.booking?.status === 'completed' && 
               material.booking?.end_date && 
               isDateInRange(material.booking.end_date, dateRange);
      });

      // Calculate total job materials costs (sum of all total_cost from booking_materials)
      const totalJobMaterials = filteredMaterials.reduce((sum, material) => {
        return sum + (material.total_cost || 0);
      }, 0);

      return {
        value: formatCurrency(totalJobMaterials),
        label: `Total Job Materials`
      };
    } catch (error) {
      console.error('Error fetching job materials data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Total Job Materials`
      };
    }
  };

  // Function to fetch real labour data from the database
  const fetchLabourData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Get total labor costs (exact same logic as ProfitLoss.tsx)
      // Fetch flat rate entries
      const { data: flatRateData, error: flatRateError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'flat_rate')
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', endDate.toISOString());

      if (flatRateError) {
        console.error('Error fetching flat rate data for labour:', flatRateError);
        throw flatRateError;
      }

      // Fetch timeclock entries
      const { data: timeclockData, error: timeclockError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          clock_out,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'timeclock')
        .gte('clock_out', startDate.toISOString())
        .lte('clock_out', endDate.toISOString());

      if (timeclockError) {
        console.error('Error fetching timeclock data for labour:', timeclockError);
        throw timeclockError;
      }

      // Fetch commission entries
      const { data: commissionData, error: commissionError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'commission')
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', endDate.toISOString());

      if (commissionError) {
        console.error('Error fetching commission data for labour:', commissionError);
        throw commissionError;
      }

      // Fetch bonus entries
      const { data: bonusData, error: bonusError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          bonus_earnings,
          total_earnings,
          created_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'bonus')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (bonusError) {
        console.error('Error fetching bonus data for labour:', bonusError);
        throw bonusError;
      }

      // Debug logging for labor report bonus data
      console.log('🔍 KPI Labor Report: Fetched bonus data:', bonusData);
      console.log('🔍 KPI Labor Report: Bonus entries count:', bonusData?.length || 0);
      if (bonusData && bonusData.length > 0) {
        const totalBonuses = bonusData.reduce((sum, entry) => sum + (entry.bonus_earnings || entry.total_earnings || 0), 0);
        console.log('💰 KPI Labor Report: Total bonus costs for period:', totalBonuses);
      }

      // Combine all four types of payroll data
      const combinedLaborData = [
        ...(flatRateData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(timeclockData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(commissionData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(bonusData?.map(entry => ({
          total_earnings: entry.bonus_earnings || entry.total_earnings
        })) || [])
      ];

      const totalLaborCosts = combinedLaborData.reduce((sum, entry) => {
        return sum + (entry.total_earnings || 0);
      }, 0);

      return {
        value: formatCurrency(totalLaborCosts),
        label: `Total Labour Costs`
      };
    } catch (error) {
      console.error('Error fetching labour data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Total Labour Costs`
      };
    }
  };

  // Function to fetch total jobs data from the database
  const fetchTotalJobsData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Get all non-deleted bookings within the date range regardless of status
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('id, end_date, status')
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (error) {
        console.error('Error fetching total jobs data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredBookings = (bookings || []).filter(booking => {
        return booking.end_date && isDateInRange(booking.end_date, dateRange);
      });

      const totalJobs = filteredBookings.length;

      return {
        value: totalJobs.toString(),
        label: `Total Jobs Booked`
      };
    } catch (error) {
      console.error('Error fetching total jobs data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Total Jobs Booked`
      };
    }
  };

  // Function to fetch completed jobs data from the database
  const fetchCompletedJobsData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Get only completed non-deleted bookings within the date range
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('id, end_date, status')
        .eq('business_id', businessId)
        .eq('status', 'completed')
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (error) {
        console.error('Error fetching completed jobs data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredCompletedBookings = (bookings || []).filter(booking => {
        return booking.status === 'completed' && 
               booking.end_date && 
               isDateInRange(booking.end_date, dateRange);
      });

      const completedJobs = filteredCompletedBookings.length;

      return {
        value: completedJobs.toString(),
        label: `Completed Jobs`
      };
    } catch (error) {
      console.error('Error fetching completed jobs data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Completed Jobs`
      };
    }
  };

  // Function to fetch real net profit data from the database (matches ProfitLoss.tsx calculation)
  const fetchNetProfitData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // 1. Get total revenue after discounts from completed bookings (same as ProfitLoss.tsx)
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('status', 'completed')
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (bookingsError) {
        console.error('Error fetching bookings for net profit:', bookingsError);
        throw bookingsError;
      }

      // Calculate total revenue after discounts
      const totalRevenue = (bookings || []).reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0);

      // 2. Get total labor costs (exact same logic as ProfitLoss.tsx)
      // Fetch flat rate entries
      const { data: flatRateData, error: flatRateError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'flat_rate')
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', endDate.toISOString());

      if (flatRateError) {
        console.error('Error fetching flat rate data for net profit:', flatRateError);
        throw flatRateError;
      }

      // Fetch timeclock entries
      const { data: timeclockData, error: timeclockError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          clock_out,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'timeclock')
        .gte('clock_out', startDate.toISOString())
        .lte('clock_out', endDate.toISOString());

      if (timeclockError) {
        console.error('Error fetching timeclock data for net profit:', timeclockError);
        throw timeclockError;
      }

      // Fetch commission entries
      const { data: commissionData, error: commissionError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'commission')
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', endDate.toISOString());

      if (commissionError) {
        console.error('Error fetching commission data for net profit:', commissionError);
        throw commissionError;
      }

      // Fetch bonus entries
      const { data: bonusData, error: bonusError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          bonus_earnings,
          total_earnings,
          created_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'bonus')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (bonusError) {
        console.error('Error fetching bonus data for net profit:', bonusError);
        throw bonusError;
      }

      // Debug logging for bonus data
      console.log('🔍 KPI Dashboard: Fetched bonus data:', bonusData);
      console.log('🔍 KPI Dashboard: Bonus entries count:', bonusData?.length || 0);
      if (bonusData && bonusData.length > 0) {
        const totalBonuses = bonusData.reduce((sum, entry) => sum + (entry.bonus_earnings || entry.total_earnings || 0), 0);
        console.log('💰 KPI Dashboard: Total bonus costs for period:', totalBonuses);
      }

      // Combine all four types of payroll data
      const combinedLaborData = [
        ...(flatRateData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(timeclockData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(commissionData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(bonusData?.map(entry => ({
          total_earnings: entry.bonus_earnings || entry.total_earnings
        })) || [])
      ];

      const totalLaborCosts = combinedLaborData.reduce((sum, entry) => {
        return sum + (entry.total_earnings || 0);
      }, 0);

      // 3. Get total supply costs from stock purchases (same logic as ProfitLoss.tsx)
      const { data: stockTransactions, error: stockError } = await supabase
        .from('stock_transactions')
        .select(`
          quantity,
          price,
          created_at,
          type,
          stock_items!inner(business_id)
        `)
        .eq('type', 'purchase')
        .eq('stock_items.business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (stockError) {
        console.error('Error fetching stock data for net profit:', stockError);
        throw stockError;
      }

      const totalSupplyCosts = (stockTransactions || []).reduce((sum, transaction) => {
        const quantity = Math.abs(Number(transaction.quantity) || 0);
        const price = Number(transaction.price) || 0;
        return sum + (quantity * price);
      }, 0);

      // 4. Get fixed expenses from operating costs (same logic as ProfitLoss.tsx)
      const { data: operatingCosts, error: operatingCostsError } = await supabase
        .from('operating_costs')
        .select('amount')
        .eq('business_id', businessId);

      if (operatingCostsError) {
        console.error('Error fetching operating costs for net profit:', operatingCostsError);
        throw operatingCostsError;
      }

      const fixedExpenses = (operatingCosts || []).reduce((sum, expense) => {
        return sum + (Number(expense.amount) || 0);
      }, 0);

      // 5. Calculate Net Profit (same formula as ProfitLoss.tsx)
      const netProfit = totalRevenue - (totalLaborCosts + totalSupplyCosts + fixedExpenses);

      return {
        value: formatCurrency(netProfit, true),
        label: `Bottom Line Profit`
      };
    } catch (error) {
      console.error('Error fetching net profit data:', error);
      // Return fallback data on error
      return {
        value: '+$0',
        label: `Bottom Line Profit`
      };
    }
  };

  // Function to fetch stock alert data - live current stock health
  const fetchStockAlertData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string; status: 'healthy' | 'low' }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // Query all stock items for the business (excluding deleted items)
      const { data: stockItems, error } = await supabase
        .from('stock_items')
        .select('id, name, quantity, reorder_point, supplier_id, max_quantity, acquisition_cost, unit')
        .eq('business_id', businessId)
        .neq('is_deleted', true);

      if (error) {
        console.error('Error fetching stock data:', error);
        throw error;
      }

      // Update state with stock items for supplier order calculations
      setStockItems(stockItems || []);

      // Check if there are any items below reorder point
      // Note: We count the number of distinct items that are low, not the total quantity needed
      const lowStockItems = (stockItems || []).filter(item => 
        item.quantity <= item.reorder_point
      );

      const totalItems = stockItems?.length || 0;
      const lowStockCount = lowStockItems.length; // Count of distinct items needing reorder

      if (lowStockCount > 0) {
        // Low stock situation - show count of items needing reorder
        return {
          value: lowStockCount.toString(),
          label: `Item${lowStockCount > 1 ? 's' : ''} Need${lowStockCount === 1 ? 's' : ''} Reorder`,
          status: 'low'
        };
      } else {
        // Healthy stock situation - show 0 (no items need reordering)
        return {
          value: '0',
          label: 'Items Need Reorder',
          status: 'healthy'
        };
      }
    } catch (error) {
      console.error('Error fetching stock alert data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: 'Stock Status Unknown',
        status: 'healthy'
      };
    }
  };

  // Function to fetch proposals sent data from the database
  const fetchProposalsSentData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Count proposals that have been sent (including viewed, approved, declined, booked)
      // These statuses all indicate the proposal was sent at some point
      const { data: proposals, error } = await supabase
        .from('proposals')
        .select('id, status, created_at')
        .eq('business_id', businessId)
        .in('status', ['sent', 'viewed', 'approved', 'declined', 'booked'])
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching proposals sent data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredProposals = (proposals || []).filter(proposal => {
        return proposal.created_at && isDateInRange(proposal.created_at.split('T')[0], dateRange);
      });

      const totalProposalsSent = filteredProposals.length;

      return {
        value: totalProposalsSent.toString(),
        label: `Proposals Sent`
      };
    } catch (error) {
      console.error('Error fetching proposals sent data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Proposals Sent`
      };
    }
  };

  // Function to fetch proposals booked data from the database
  const fetchProposalsBookedData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query proposals with status 'booked' within the date range
      const { data: proposals, error } = await supabase
        .from('proposals')
        .select('id, status, created_at')
        .eq('business_id', businessId)
        .eq('status', 'booked')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching proposals booked data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredProposals = (proposals || []).filter(proposal => {
        return proposal.created_at && isDateInRange(proposal.created_at.split('T')[0], dateRange);
      });

      const totalProposalsBooked = filteredProposals.length;

      return {
        value: totalProposalsBooked.toString(),
        label: `Proposals Booked`
      };
    } catch (error) {
      console.error('Error fetching proposals booked data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Proposals Booked`
      };
    }
  };

  // Function to fetch team rating data from the database
  const fetchTeamRatingData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Use the same function as TeamReports page to get team rating
      const { getDynamicBusinessFeedbackReport } = await import('../lib/feedback');
      
      const dateRangeFilter = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };

      const feedbackReport = await getDynamicBusinessFeedbackReport(businessId, dateRangeFilter);

      if (!feedbackReport || feedbackReport.total_feedback_count === 0) {
        return {
          value: '0.0',
          label: `Team Rating`
        };
      }

      // Format the rating to 1 decimal place
      const rating = feedbackReport.overall_business_rating.toFixed(1);

      return {
        value: rating,
        label: `Team Rating (${feedbackReport.total_feedback_count} review${feedbackReport.total_feedback_count !== 1 ? 's' : ''})`
      };
    } catch (error) {
      console.error('Error fetching team rating data:', error);
      // Return fallback data on error
      return {
        value: '0.0',
        label: `Team Rating`
      };
    }
  };

  // Function to fetch feedback percentage data from the database
  const fetchFeedbackPercentageData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Use the same function as TeamReports page to get feedback response rate
      const { getFeedbackResponseRate } = await import('../lib/feedback');
      
      const dateRangeFilter = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };

      const responseRateData = await getFeedbackResponseRate(businessId, dateRangeFilter);

      if (!responseRateData || responseRateData.completedBookings === 0) {
        return {
          value: '0%',
          label: `Feedback Response Rate`
        };
      }

      return {
        value: `${responseRateData.responseRate}%`,
        label: `Feedback Response Rate (${responseRateData.feedbackSubmissions}/${responseRateData.completedBookings})`
      };
    } catch (error) {
      console.error('Error fetching feedback percentage data:', error);
      // Return fallback data on error
      return {
        value: '0%',
        label: `Feedback Response Rate`
      };
    }
  };

  // Function to fetch sales graph data from the database
  const fetchSalesGraphData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string; chartData?: any }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Fetch non-deleted bookings for the date range (excluding cancelled)
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0])
        .neq('status', 'cancelled');

      if (error) {
        console.error('Error fetching sales graph data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredBookings = (bookings || []).filter(booking => {
        return booking.end_date && isDateInRange(booking.end_date, dateRange);
      });

      // Group sales by date or month based on date range
      let salesData, chartLabels, datasetLabel;
      
      if (dateRange === 'this_year') {
        // For yearly view: group by month and ensure all 12 months are present
        const salesByMonth = filteredBookings.reduce((acc: Record<string, number>, booking) => {
          if (!booking.end_date) return acc;
          
          // Convert to UTC date and get year-month key (YYYY-MM)
          const date = new Date(booking.end_date + 'T00:00:00Z');
          const monthKey = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
          
          if (!acc[monthKey]) {
            acc[monthKey] = 0;
          }
          acc[monthKey] += (booking.subtotal || 0) - (booking.discount_amount || 0);
          return acc;
        }, {});

        // Generate all 12 months for current year
        const currentYear = new Date().getFullYear();
        const allMonths = Array.from({ length: 12 }, (_, i) => {
          const month = String(i + 1).padStart(2, '0');
          return `${currentYear}-${month}`;
        });

        // Ensure all months have data (0 if no sales)
        allMonths.forEach(month => {
          if (!salesByMonth[month]) {
            salesByMonth[month] = 0;
          }
        });

        salesData = allMonths.map(month => salesByMonth[month]);
        chartLabels = allMonths.map(month => {
          const [year, monthNum] = month.split('-');
          const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
          return date.toLocaleDateString('en-US', { 
            month: 'short',
            timeZone: 'UTC'
          });
        });
        datasetLabel = 'Monthly Sales';
      } else {
        // For monthly/weekly views: group by day (existing behavior)
        const salesByDate = filteredBookings.reduce((acc: Record<string, number>, booking) => {
          if (!booking.end_date) return acc;
          
          // Convert to UTC date string (YYYY-MM-DD)
          const date = new Date(booking.end_date + 'T00:00:00Z');
          const dateKey = date.toISOString().split('T')[0];
          
          if (!acc[dateKey]) {
            acc[dateKey] = 0;
          }
          acc[dateKey] += (booking.subtotal || 0) - (booking.discount_amount || 0);
          return acc;
        }, {});

        // Sort dates and prepare chart data
        const dates = Object.keys(salesByDate).sort();
        salesData = dates.map(date => salesByDate[date]);
        chartLabels = dates.map(date => {
          const d = new Date(date + 'T00:00:00Z');
          return d.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            timeZone: 'UTC'
          });
        });
        datasetLabel = 'Daily Sales';
      }

      const chartData = {
        labels: chartLabels,
        datasets: [
          {
            label: datasetLabel,
            data: salesData,
            borderColor: 'rgb(147, 51, 234)',
            backgroundColor: 'rgba(147, 51, 234, 0.1)',
            tension: 0.4,
            fill: true,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'rgb(147, 51, 234)',
            pointBorderColor: 'white',
            pointBorderWidth: 2
          }
        ]
      };

      // Calculate total sales for display
      const totalSales = salesData.reduce((sum: number, sale: number) => sum + sale, 0);

      return {
        value: formatCurrency(totalSales),
        label: `Sales Trend`,
        chartData
      };
    } catch (error) {
      console.error('Error fetching sales graph data:', error);
      // Return fallback data on error
      return {
        value: '$0.00',
        label: `Sales Trend`,
        chartData: {
          labels: [],
          datasets: [{
            label: 'Daily Sales',
            data: [],
            borderColor: 'rgb(147, 51, 234)',
            backgroundColor: 'rgba(147, 51, 234, 0.1)',
          }]
        }
      };
    }
  };

  // Function to fetch average sale data from the database
  const fetchAverageSaleData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query non-deleted bookings within the date range, excluding cancelled bookings
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0])
        .neq('status', 'cancelled');

      if (error) {
        console.error('Error fetching average sale data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredBookings = (bookings || []).filter(booking => {
        return booking.end_date && isDateInRange(booking.end_date, dateRange);
      });

      if (filteredBookings.length === 0) {
        return {
          value: '$0.00',
          label: `Average Sale`
        };
      }

      // Calculate total sales after discounts
      const totalSales = filteredBookings.reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0);

      // Calculate average sale
      const averageSale = totalSales / filteredBookings.length;

      return {
        value: formatCurrency(averageSale),
        label: `Average Sale (${filteredBookings.length} booking${filteredBookings.length !== 1 ? 's' : ''})`
      };
    } catch (error) {
      console.error('Error fetching average sale data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Average Sale`
      };
    }
  };

  // Function to fetch booking source data for pie chart
  const fetchBookingSourceData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string; chartData?: any; isPieChart?: boolean }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Query bookings within the date range, excluding cancelled bookings
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('source, end_date, status')
        .eq('business_id', businessId)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0])
        .neq('status', 'cancelled');

      if (error) {
        console.error('Error fetching booking source data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredBookings = (bookings || []).filter(booking => {
        return booking.end_date && isDateInRange(booking.end_date, dateRange);
      });

      if (filteredBookings.length === 0) {
        return {
          value: '0',
          label: 'Total Bookings',
          chartData: null,
          isPieChart: true
        };
      }

      // Count bookings by source
      const sourceCounts: Record<string, number> = {};
      filteredBookings.forEach(booking => {
        const source = booking.source || 'Undefined';
        sourceCounts[source] = (sourceCounts[source] || 0) + 1;
      });

      // Sort sources by count (descending - highest to lowest)
      const sortedSources = Object.entries(sourceCounts)
        .sort(([, a], [, b]) => b - a);

      // Prepare pie chart data (now sorted from highest to lowest %)
      const labels = sortedSources.map(([source]) => source);
      const data = sortedSources.map(([, count]) => count);
      const total = filteredBookings.length;

      // Generate colors for each slice
      const colors = [
        '#3B82F6', // Blue
        '#10B981', // Green  
        '#F59E0B', // Yellow
        '#EF4444', // Red
        '#8B5CF6', // Purple
        '#06B6D4', // Cyan
        '#F97316', // Orange
        '#84CC16', // Lime
        '#EC4899', // Pink
        '#6B7280'  // Gray
      ];

      const chartData = {
        labels: labels.map(label => {
          const count = sourceCounts[label];
          const percentage = ((count / total) * 100).toFixed(1);
          return `${label} (${percentage}%)`;
        }),
        datasets: [{
          data: data,
          backgroundColor: colors.slice(0, labels.length),
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverBorderWidth: 3
        }]
      };

      return {
        value: total.toString(),
        label: `Total Bookings`,
        chartData: chartData,
        isPieChart: true
      };
    } catch (error) {
      console.error('Error fetching booking source data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: 'Total Bookings',
        chartData: null,
        isPieChart: true
      };
    }
  };

  // Function to fetch online booking funnel data
  const fetchOnlineBookingVolumeData = async (businessId: string, dateRange: DateRangeOption): Promise<{ value: string; label: string; funnelData?: any; bannerSubmits?: number; showBannerSubmits?: boolean }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange);
      
      // Check if popup is enabled for this business
      const { data: popupSettings, error: popupError } = await supabase
        .from('online_booking_settings')
        .select('popup_enabled')
        .eq('business_id', businessId)
        .single();

      const isPopupEnabled = popupSettings?.popup_enabled || false;
      
      // Query booking step progression within the date range
      const { data: stepData, error } = await supabase
        .from('booking_step_progression')
        .select('step, session_id')
        .eq('business_id', businessId)
        .gte('reached_at', startDate.toISOString())
        .lte('reached_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching booking funnel data:', error);
        throw error;
      }

      // Query banner submissions if popup is enabled
      let bannerSubmits = 0;
      if (isPopupEnabled) {
        const { data: bannerData, error: bannerError } = await supabase
          .from('banner_submissions')
          .select('id')
          .eq('business_id', businessId)
          .gte('submitted_at', startDate.toISOString())
          .lte('submitted_at', endDate.toISOString());
        
        if (!bannerError) {
          bannerSubmits = bannerData?.length || 0;
        }
      }

      // Process funnel data
      const stepCounts = {
        location: new Set(),
        category: new Set(),
        service: new Set(),
        datetime: new Set(),
        customer: new Set(),
        completed: new Set()
      };

      // Count unique sessions that reached each step
      (stepData || []).forEach(item => {
        if (stepCounts[item.step as keyof typeof stepCounts]) {
          stepCounts[item.step as keyof typeof stepCounts].add(item.session_id);
        }
      });

      const funnel = [
        { step: 'Location', count: stepCounts.location.size, icon: 'MapPin' },
        { step: 'Category', count: stepCounts.category.size, icon: 'Tag' },
        { step: 'Service', count: stepCounts.service.size, icon: 'Wrench' },
        { step: 'Date & Time', count: stepCounts.datetime.size, icon: 'Calendar' },
        { step: 'Customer Info', count: stepCounts.customer.size, icon: 'User' },
        { step: 'Completed', count: stepCounts.completed.size, icon: 'CheckCircle' }
      ];

      // Don't add banner submits to funnel - they'll be displayed separately

      const totalStarts = stepCounts.location.size;
      const totalCompletions = stepCounts.completed.size;
      const conversionRate = totalStarts > 0 ? ((totalCompletions / totalStarts) * 100).toFixed(1) : '0.0';

      return {
        value: `${conversionRate}%`,
        label: `Conversion Rate (${totalCompletions}/${totalStarts})`,
        funnelData: funnel,
        bannerSubmits: bannerSubmits,
        showBannerSubmits: isPopupEnabled
      };
    } catch (error) {
      console.error('Error fetching booking funnel data:', error);
      // Return fallback data on error
      return {
        value: '0.0%',
        label: 'Conversion Rate (0/0)',
        funnelData: [
          { step: 'Location', count: 0, icon: 'MapPin' },
          { step: 'Category', count: 0, icon: 'Tag' },
          { step: 'Service', count: 0, icon: 'Wrench' },
          { step: 'Date & Time', count: 0, icon: 'Calendar' },
          { step: 'Customer Info', count: 0, icon: 'User' },
          { step: 'Completed', count: 0, icon: 'CheckCircle' }
        ],
        bannerSubmits: 0,
        showBannerSubmits: false
      };
    }
  };

  // Load suppliers for stock alert functionality
  const loadSuppliers = useCallback(async () => {
    if (!businessId) return;
    
    try {
      const data = await getSuppliers(businessId);
      setSuppliers(data);
    } catch (err) {
      console.error('Error loading suppliers:', err);
    }
  }, [businessId]);

  // Load pending orders for stock alert functionality
  const loadPendingOrders = useCallback(async () => {
    if (!businessId) return;
    
    try {
      const pendingItems = await getPendingOrdersForItems(businessId);
      
      // Convert to a Map for easy lookup
      const itemMap = new Map<string, number>();
      pendingItems.forEach(item => {
        itemMap.set(item.itemId, item.quantityOrdered);
      });
      
      setPendingOrderItems(itemMap);
      
    } catch (err) {
      console.error('Error loading pending orders:', err);
    }
  }, [businessId]);

  // Calculate supplier orders for stock alert
  const calculateSupplierOrders = useCallback(() => {
    if (!stockItems.length || !suppliers.length) return;

    // Filter items that are below reorder point and have a supplier
    const itemsToOrder = stockItems.filter(item => {
      const isLowStock = item.quantity <= item.reorder_point;
      const hasSupplier = !!item.supplier_id;
      const canAddMoreStock = item.max_quantity > item.quantity;
      
      // Check if the item is already in a pending order
      const pendingQuantity = pendingOrderItems.get(item.id) || 0;
      const projectedQuantity = item.quantity + pendingQuantity;
      const stillNeedsOrdering = projectedQuantity <= item.reorder_point;
      
      return isLowStock && hasSupplier && canAddMoreStock && stillNeedsOrdering;
    });
    
    // Group by supplier
    const ordersBySupplier: Record<string, SupplierOrder> = {};
    
    itemsToOrder.forEach(item => {
      if (!item.supplier_id) return;
      
      const supplier = suppliers.find(s => s.id === item.supplier_id);
      if (!supplier) return;
      
      // Calculate how much quantity to order considering pending orders
      const pendingQuantity = pendingOrderItems.get(item.id) || 0;
      const effectiveQuantity = item.quantity + pendingQuantity;
      const quantityToOrder = item.max_quantity - effectiveQuantity;
      
      if (quantityToOrder <= 0) return;
      
      const totalCost = quantityToOrder * item.acquisition_cost;
      
      if (!ordersBySupplier[supplier.id]) {
        ordersBySupplier[supplier.id] = {
          supplier,
          items: [],
          totalCost: 0,
          itemCount: 0
        };
      }
      
      ordersBySupplier[supplier.id].items.push({
        item,
        quantityToOrder,
        totalCost
      });
      
      ordersBySupplier[supplier.id].totalCost += totalCost;
      ordersBySupplier[supplier.id].itemCount += 1;
    });
    
    // Convert to array and sort by supplier name
    const ordersArray = Object.values(ordersBySupplier)
      .sort((a, b) => {
        const aName = `${a.supplier.first_name} ${a.supplier.last_name}`.trim();
        const bName = `${b.supplier.first_name} ${b.supplier.last_name}`.trim();
        return aName.localeCompare(bName);
      });
    
    setSupplierOrders(ordersArray);
  }, [stockItems, suppliers, pendingOrderItems]);

  // Handle placing an order
  const handlePlaceOrder = async (supplierId: string) => {
    const orderGroup = supplierOrders.find(o => o.supplier.id === supplierId);
    if (!orderGroup || !businessId) {
      console.error('Order group not found for supplier', supplierId);
      return;
    }
    
    try {
      // Create order number and get current date
      const orderNumber = `PO-${Date.now().toString().slice(-6)}`;
      
      // Prepare order items for the database
      const dbOrderItems: DBOrderItem[] = orderGroup.items.map(item => ({
        item_id: item.item.id,
        quantity: item.quantityToOrder,
        unit_price: item.item.acquisition_cost,
        total_price: item.totalCost,
        item_name: item.item.name,
        item_unit: item.item.unit
      }));
      
      // Create the order in the database
      await createOrder(
        {
          business_id: businessId,
          supplier_id: orderGroup.supplier.id,
          order_number: orderNumber,
          total_amount: orderGroup.totalCost,
          status: 'pending'
        },
        dbOrderItems
      );
      
      // Show success notification
      toast.success(`Order ${orderNumber} placed successfully!`);
      
      // Refresh data
      await loadPendingOrders();
      calculateSupplierOrders();
      
    } catch (err) {
      console.error('Error sending order:', err);
             toast.error('Failed to place order: ' + (err instanceof Error ? err.message : 'Unknown error'));
     }
   };

  // Load suppliers and pending orders when business changes
  useEffect(() => {
    if (businessId) {
      loadSuppliers();
      loadPendingOrders();
    }
  }, [businessId, loadSuppliers, loadPendingOrders]);

  // Calculate supplier orders when data changes
  useEffect(() => {
    if (stockItems.length > 0 && suppliers.length > 0) {
      calculateSupplierOrders();
    }
  }, [stockItems, suppliers, pendingOrderItems, calculateSupplierOrders]);

  // Get the previous period for comparison based on current period
  const getPreviousPeriod = (currentPeriod: DateRangeOption): DateRangeOption => {
    switch (currentPeriod) {
      case 'this_month':
        return 'last_month';
      case 'last_month':
        // Previous period for last month would be two months ago
        // For simplicity, we'll compare with the month before last month
        return 'this_month'; // Fallback comparison
      case 'next_month':
        return 'this_month';
      case 'this_week':
        return 'last_week';
      case 'last_week':
        // For last week, compare with the week before that
        return 'this_week'; // Fallback comparison
      case 'next_week':
        return 'this_week';
      case 'this_year':
        return 'last_month'; // Fallback comparison (ideally would be 'last_year')
      default:
        return 'last_month'; // Default fallback
    }
  };

  // Calculate percentage change between current and previous values
  const calculatePercentageChange = (current: string, previous: string): { percentage: number; direction: 'up' | 'down' | 'same' } => {
    // Extract numeric values from strings (handles currency, percentages, etc.)
    const extractNumber = (str: string): number => {
      // Remove currency symbols, commas, and other non-numeric characters except decimal points and negative signs
      const cleaned = str.replace(/[$,%+\s]/g, '').replace(/[^-\d.]/g, '');
      return parseFloat(cleaned) || 0;
    };

    const currentValue = extractNumber(current);
    const previousValue = extractNumber(previous);

    if (previousValue === 0) {
      // If previous value is 0, any current value is either 0% (if current is also 0) or 100% increase
      return currentValue === 0 
        ? { percentage: 0, direction: 'same' }
        : { percentage: 100, direction: 'up' };
    }

    const change = ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
    
    return {
      percentage: Math.abs(change),
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'same'
    };
  };

  // Fetch report data with comparison to previous period
  const getFilteredReportDataWithComparison = async (reportType: string, dateRange: DateRangeOption, businessId: string) => {
    // Get current period data
    const currentData = await getFilteredReportData(reportType, dateRange, businessId);
    
    // Skip comparison for special report types that don't need it
    if (reportType === 'title' || reportType === 'stock_alert' || reportType === 'online_booking_volume' || reportType === 'team_rating' || reportType === 'sales_graph') {
      return currentData;
    }

    try {
      // Get previous period data for comparison
      const previousPeriod = getPreviousPeriod(dateRange);
      const previousData = await getFilteredReportData(reportType, previousPeriod, businessId);
      
      // Calculate comparison if both values are available
      if (currentData && 'value' in currentData && previousData && 'value' in previousData) {
        const comparison = calculatePercentageChange(currentData.value, previousData.value);
        
        return {
          ...currentData,
          comparison: {
            percentage: comparison.percentage,
            direction: comparison.direction,
            previousValue: previousData.value
          }
        };
      }
    } catch (error) {
      console.error('Error fetching comparison data:', error);
      // Return current data without comparison if there's an error
    }

    return currentData;
  };

  // Generate filtered report data based on date range
  const getFilteredReportData = async (reportType: string, dateRange: DateRangeOption, businessId: string) => {
    if (reportType === 'sales') {
      // Use real sales data for sales reports
      return await fetchSalesData(businessId, dateRange);
    }
    
    if (reportType === 'upsells') {
      // Use real upsells data for upsells reports
      return await fetchUpsellsData(businessId, dateRange);
    }
    
    if (reportType === 'closed_sales') {
      // Use real closed sales data for closed sales reports
      return await fetchClosedSalesData(businessId, dateRange);
    }
    
    if (reportType === 'supplies_purchased') {
      // Use real supplies purchased data for supplies purchased reports
      return await fetchSuppliesPurchasedData(businessId, dateRange);
    }
    
    if (reportType === 'net_profit') {
      // Use real net profit data for net profit reports
      return await fetchNetProfitData(businessId, dateRange);
    }
    
    if (reportType === 'labour') {
      // Use real labour data for labour reports
      return await fetchLabourData(businessId, dateRange);
    }
    
    if (reportType === 'job_materials') {
      // Use real job materials data for job materials reports
      return await fetchJobMaterialsData(businessId, dateRange);
    }
    
    if (reportType === 'total_jobs') {
      // Use real total jobs data for total jobs reports
      return await fetchTotalJobsData(businessId, dateRange);
    }
    
    if (reportType === 'completed_jobs') {
      // Use real completed jobs data for completed jobs reports
      return await fetchCompletedJobsData(businessId, dateRange);
    }

    if (reportType === 'stock_alert') {
      // Use real stock alert data for stock alert reports
      return await fetchStockAlertData(businessId, dateRange);
    }

    if (reportType === 'online_booking_volume') {
      // Use real online booking volume data for online booking volume reports
      return await fetchOnlineBookingVolumeData(businessId, dateRange);
    }

    if (reportType === 'proposals_sent') {
      // Use real proposals sent data for proposals sent reports
      return await fetchProposalsSentData(businessId, dateRange);
    }

    if (reportType === 'proposals_booked') {
      // Use real proposals booked data for proposals booked reports
      return await fetchProposalsBookedData(businessId, dateRange);
    }

    if (reportType === 'team_rating') {
      // Use real team rating data for team rating reports
      return await fetchTeamRatingData(businessId, dateRange);
    }

    if (reportType === 'feedback_percentage') {
      // Use real feedback percentage data for feedback percentage reports
      return await fetchFeedbackPercentageData(businessId, dateRange);
    }

    if (reportType === 'sales_graph') {
      // Use real sales graph data for sales graph reports
      return await fetchSalesGraphData(businessId, dateRange);
    }

    if (reportType === 'average_sale') {
      // Use real average sale data for average sale reports
      return await fetchAverageSaleData(businessId, dateRange);
    }
    
    if (reportType === 'booking_source') {
      // Use real booking source data for booking source reports
      return await fetchBookingSourceData(businessId, dateRange);
    }
    
    if (reportType === 'website_visits') {
      // Use real website visits data for website visits reports
      return await fetchWebsiteVisitsData(businessId, dateRange);
    }
    
    if (reportType === 'google_reviews') {
      // Use real Google reviews data for Google reviews reports
      return await fetchGoogleReviewsData(businessId, dateRange);
    }
    
    if (reportType === 'phone_calls') {
      // Use real phone calls data for phone calls reports
      return await fetchPhoneCallsData(businessId, dateRange);
    }
    
    if (reportType === 'missed_calls') {
      // Use real missed calls data for missed calls reports
      return await fetchMissedCallsData(businessId, dateRange);
    }
    
    if (reportType === 'title') {
      // For title elements, return text data instead of value/label
      return {
        text: 'Dashboard Title',
        isTitle: true
      };
    }
    
    // For other report types, use simulated data for now
    const baseValue = 24567;
    const multiplier = dateRange.includes('month') ? 1 : 0.25; // Weeks are roughly 1/4 of month
    const randomFactor = 0.8 + Math.random() * 0.4; // Random variation between 0.8 and 1.2
    
    const filteredValue = Math.round(baseValue * multiplier * randomFactor);
    
    return {
      value: `$${filteredValue.toLocaleString()}`,
      label: `Total Sales After Discounts`
    };
  };

  const handleReportClick = async (reportType: string, reportTitle: string) => {
    if (!selectedDashboardId || !businessId) return;

    try {
      const selectedDashboard = dashboards.find(d => d.id === selectedDashboardId);
      const dateRange = selectedDashboard?.dateRange || 'this_month';
      const filteredData = await getFilteredReportDataWithComparison(reportType, dateRange, businessId);
      
      const existingReports = selectedDashboard?.reports || [];
      const nextPosition = existingReports.length;

      // Set different default sizes for different report types based on view type
      let defaultWidth, defaultHeight;
      
      if (currentViewType === 'mobile') {
        // Mobile: 20-column grid system with 8px row height - optimized for mobile usage patterns
        if (reportType === 'stock_alert') {
          defaultWidth = 20; // Full width for stock alert - better on mobile
          defaultHeight = 40; // Taller for better readability on mobile
        } else if (reportType === 'online_booking_volume') {
          defaultWidth = 20; // Full width for funnel display - better touch interaction
          defaultHeight = 35; // Increased height for better mobile viewing
        } else if (reportType === 'sales_graph') {
          defaultWidth = 20; // Full width for sales graph - essential for mobile
          defaultHeight = 32; // Increased height for better chart readability
        } else if (reportType === 'total_revenue' || reportType === 'total_bookings' || reportType === 'average_sale') {
          defaultWidth = 20; // Full width for key metrics on mobile - better visibility
          defaultHeight = 25; // Optimized height for key data display
        } else {
          defaultWidth = 20; // Full width for all reports on mobile - better UX
          defaultHeight = 28; // Increased from 22 for better mobile readability
        }
      } else {
        // Desktop: 100-column grid system with 10px row height
        if (reportType === 'stock_alert') {
          defaultWidth = 40; // 40% width for stock alert
          defaultHeight = 35; // Taller for stock alert content
        } else if (reportType === 'online_booking_volume') {
          defaultWidth = 30; // 30% width for funnel display
          defaultHeight = 30; // Tall enough for funnel steps
        } else {
          defaultWidth = 20; // 20% width for others
          defaultHeight = 25; // Standard height
        }
      }

      const reportData = {
        dashboard_id: selectedDashboardId,
        business_id: businessId,
        report_type: reportType,
        report_title: reportTitle,
        layout_x: currentViewType === 'mobile' ? (nextPosition * defaultWidth) % 20 : (nextPosition * defaultWidth) % 100, // Mobile: use 20-column grid, Desktop: use 100-column grid
        layout_y: currentViewType === 'mobile' ? Math.floor((nextPosition * defaultWidth) / 20) * defaultHeight : Math.floor((nextPosition * defaultWidth) / 100) * defaultHeight, // Mobile: wrap after 20 columns, Desktop: wrap after 100
        layout_w: defaultWidth, // Variable width based on report type
        layout_h: defaultHeight, // Variable height based on report type
        view_type: currentViewType, // Add current view type
        report_data: filteredData
      };

      const createdReport = await createDashboardReport(reportData);

      const newReport: Report = {
        id: createdReport.id,
        type: createdReport.report_type,
        title: createdReport.report_title,
        data: createdReport.report_data,
        layout: {
          x: createdReport.layout_x,
          y: createdReport.layout_y,
          w: createdReport.layout_w,
          h: createdReport.layout_h
        }
      };

      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === selectedDashboardId) {
          return {
            ...dashboard,
            reports: [...(dashboard.reports || []), newReport]
          };
        }
        return dashboard;
      }));

      // If it's a title element, initialize its text in our state
      if (reportType === 'title') {
        const titleText = 'text' in filteredData ? filteredData.text : 'Dashboard Title';
        setReportTitleTexts(prev => ({
          ...prev,
          [createdReport.id]: titleText
        }));
      }

      setIsAddReportsModalOpen(false);
      setSelectedDashboardId(null);
      toast.success(`${reportTitle} ${reportType === 'title' ? 'element' : 'report'} added to ${currentViewType} view`);
    } catch (error) {
      console.error('Error adding report:', error);
      toast.error('Failed to add report');
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setDashboardName('');
  };

  const handleAddReportsClick = (dashboardId: string) => {
    setSelectedDashboardId(dashboardId);
    setIsAddReportsModalOpen(true);
  };

  const handleAddReportsModalClose = () => {
    setIsAddReportsModalOpen(false);
    setSelectedDashboardId(null);
  };

  const toggleEditMode = () => {
    if (!isEditMode) {
      // Entering edit mode - save current state as original
      const currentDashboardTitles: {[key: string]: string} = {};
      const currentReportTitles: {[key: string]: string} = {};
      const currentLayouts: {[key: string]: any[]} = {};
      
      dashboards.forEach(dashboard => {
        currentDashboardTitles[dashboard.id] = dashboard.name;
        currentLayouts[dashboard.id] = dashboard.reports?.map(report => ({
          i: report.id,
          x: report.layout.x,
          y: report.layout.y,
          w: report.layout.w,
          h: report.layout.h
        })) || [];
        
        dashboard.reports?.forEach(report => {
          currentReportTitles[report.id] = report.data?.text || '';
        });
      });
      
      setOriginalState({
        dashboardTitles: currentDashboardTitles,
        reportTitles: currentReportTitles,
        layouts: currentLayouts
      });
      
      // Initialize pending changes with current values
      setPendingDashboardTitles(currentDashboardTitles);
      setPendingReportTitles(currentReportTitles);
      setPendingLayouts(currentLayouts);
    }
    
    setIsEditMode(!isEditMode);
    // Cancel any ongoing title edits when toggling edit mode
    setEditingTitleId(null);
    setEditingTitle('');
  };

  const handleStartTitleEdit = (dashboardId: string, currentTitle: string) => {
    setEditingTitleId(dashboardId);
    setEditingTitle(currentTitle);
  };

  // Auto-save dashboard title with debouncing
  const autoSaveDashboardTitle = useCallback(async (dashboardId: string, title: string) => {
    if (!title.trim()) {
      return; // Don't save empty titles
    }

    try {
      await updateKPIDashboard(dashboardId, { title: title.trim() });
      
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            name: title.trim()
          };
        }
        return dashboard;
      }));
    } catch (error) {
      console.error('Error updating dashboard title:', error);
      toast.error('Failed to update dashboard name');
    }
  }, []);

  const handleTitleChange = (dashboardId: string, newTitle: string) => {
    setEditingTitle(newTitle);
    
    if (isEditMode) {
      // In edit mode, just update pending changes
      setPendingDashboardTitles(prev => ({
        ...prev,
        [dashboardId]: newTitle
      }));
    } else {
      // Not in edit mode, use auto-save as before
      // Clear existing timeout
      if (dashboardTitleTimeoutRef.current) {
        clearTimeout(dashboardTitleTimeoutRef.current);
      }
      
      // Set new timeout for auto-save (500ms delay)
      dashboardTitleTimeoutRef.current = setTimeout(() => {
        autoSaveDashboardTitle(dashboardId, newTitle);
      }, 500);
    }
  };

  const handleCancelTitleEdit = () => {
    // Clear any pending auto-save
    if (dashboardTitleTimeoutRef.current) {
      clearTimeout(dashboardTitleTimeoutRef.current);
    }
    setEditingTitleId(null);
    setEditingTitle('');
  };

  // Click outside handler for title editing
  const titleInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingTitleId && titleInputRef.current && !titleInputRef.current.contains(event.target as Node)) {
        handleCancelTitleEdit();
      }
    };

    if (editingTitleId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [editingTitleId]);

  // Auto-save report title with debouncing
  const autoSaveReportTitle = useCallback(async (dashboardId: string, reportId: string, titleText: string) => {
    if (!titleText.trim()) {
      return; // Don't save empty titles
    }

    try {
      // Update the database with the new title text
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const updatedData = {
        text: titleText.trim(),
        isTitle: true
      };

      const { error } = await supabase
        .from('kpi_dashboard_reports')
        .update({ report_data: updatedData })
        .eq('id', reportId);

      if (error) {
        console.error('Error updating report title in database:', error);
        throw error;
      }

      // Update the report data in the dashboard state
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            reports: dashboard.reports?.map(report => {
              if (report.id === reportId) {
                return {
                  ...report,
                  data: updatedData
                };
              }
              return report;
            })
          };
        }
        return dashboard;
      }));
    } catch (error) {
      console.error('Error updating report title:', error);
      toast.error('Failed to update title: ' + (error as Error).message);
    }
  }, []);

  const handleReportTitleChange = (dashboardId: string, reportId: string, newText: string) => {
    // Update local state immediately
    setReportTitleTexts(prev => ({
      ...prev,
      [reportId]: newText
    }));
    
    if (isEditMode) {
      // In edit mode, just update pending changes
      setPendingReportTitles(prev => ({
        ...prev,
        [reportId]: newText
      }));
    } else {
      // Not in edit mode, use auto-save as before
      // Clear existing timeout
      if (reportTitleTimeoutRef.current) {
        clearTimeout(reportTitleTimeoutRef.current);
      }
      
      // Set new timeout for auto-save (500ms delay)
      reportTitleTimeoutRef.current = setTimeout(() => {
        autoSaveReportTitle(dashboardId, reportId, newText);
      }, 500);
    }
  };

  const handleLayoutChange = async (layout: any[], dashboardId: string) => {
    if (isEditMode) {
      // In edit mode, detect collisions and update pending changes
      detectCollisions(layout, dashboardId);
      
      setPendingLayouts(prev => ({
        ...prev,
        [dashboardId]: layout
      }));
      
      // Update local state for immediate visual feedback
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            reports: dashboard.reports?.map(report => {
              const layoutItem = layout.find(item => item.i === report.id);
              if (layoutItem) {
                return {
                  ...report,
                  layout: {
                    x: layoutItem.x,
                    y: layoutItem.y,
                    w: layoutItem.w,
                    h: layoutItem.h
                  }
                };
              }
              return report;
            })
          };
        }
        return dashboard;
      }));
    } else {
      // Not in edit mode, save immediately as before
      try {
        const dashboard = dashboards.find(d => d.id === dashboardId);
        if (!dashboard?.reports) return;

        const updatePromises = dashboard.reports.map(async (report) => {
          const layoutItem = layout.find(item => item.i === report.id);
          if (layoutItem && (
            layoutItem.x !== report.layout.x ||
            layoutItem.y !== report.layout.y ||
            layoutItem.w !== report.layout.w ||
            layoutItem.h !== report.layout.h
          )) {
            await updateDashboardReportLayout(report.id, {
              layout_x: layoutItem.x,
              layout_y: layoutItem.y,
              layout_w: layoutItem.w,
              layout_h: layoutItem.h
            });
          }
        });

        await Promise.all(updatePromises);

        // Update local state
        setDashboards(prev => prev.map(dashboard => {
          if (dashboard.id === dashboardId) {
            return {
              ...dashboard,
              reports: dashboard.reports?.map(report => {
                const layoutItem = layout.find(item => item.i === report.id);
                if (layoutItem) {
                  return {
                    ...report,
                    layout: {
                      x: layoutItem.x,
                      y: layoutItem.y,
                      w: layoutItem.w,
                      h: layoutItem.h
                    }
                  };
                }
                return report;
              })
            };
          }
          return dashboard;
        }));
      } catch (error) {
        console.error('Error updating layout:', error);
        toast.error('Failed to save layout changes');
      }
    }
  };

  // Save all pending changes
  const handleSaveChanges = async () => {
    // Check for overlapping reports before saving
    if (overlappingReports.size > 0) {
      toast.error('Cannot save: Some reports are overlapping. Please resize or move them to fix the red borders.');
      return;
    }
    
    try {
      const savePromises: Promise<any>[] = [];
      
      // Save dashboard title changes
      Object.entries(pendingDashboardTitles).forEach(([dashboardId, title]) => {
        if (title !== originalState.dashboardTitles[dashboardId]) {
          savePromises.push(updateKPIDashboard(dashboardId, { title: title.trim() }));
        }
      });
      
      // Save report title changes
      Object.entries(pendingReportTitles).forEach(([reportId, text]) => {
        if (text !== originalState.reportTitles[reportId]) {
          const updatedData = {
            text: text.trim(),
            isTitle: true
          };
          savePromises.push(
            supabase.from('kpi_dashboard_reports').update({ data: updatedData }).eq('id', reportId).then()
          );
        }
      });
      
      // Save layout changes
      Object.entries(pendingLayouts).forEach(([dashboardId, layout]) => {
        const originalLayout = originalState.layouts[dashboardId];
        const dashboard = dashboards.find(d => d.id === dashboardId);
        
        if (dashboard?.reports) {
          dashboard.reports.forEach(report => {
            const newLayoutItem = layout.find(item => item.i === report.id);
            const originalLayoutItem = originalLayout?.find(item => item.i === report.id);
            
            if (newLayoutItem && originalLayoutItem && (
              newLayoutItem.x !== originalLayoutItem.x ||
              newLayoutItem.y !== originalLayoutItem.y ||
              newLayoutItem.w !== originalLayoutItem.w ||
              newLayoutItem.h !== originalLayoutItem.h
            )) {
              savePromises.push(updateDashboardReportLayout(report.id, {
                layout_x: newLayoutItem.x,
                layout_y: newLayoutItem.y,
                layout_w: newLayoutItem.w,
                layout_h: newLayoutItem.h
              }));
            }
          });
        }
      });
      
      await Promise.all(savePromises);
      
      // Update dashboards state with saved changes
      setDashboards(prev => prev.map(dashboard => {
        const newTitle = pendingDashboardTitles[dashboard.id];
        return {
          ...dashboard,
          name: newTitle || dashboard.name,
          reports: dashboard.reports?.map(report => {
            const newText = pendingReportTitles[report.id];
            return {
              ...report,
              data: newText ? { ...report.data, text: newText } : report.data
            };
          })
        };
      }));
      
      // Clear pending changes
      setPendingDashboardTitles({});
      setPendingReportTitles({});
      setPendingLayouts({});
      setReportTitleTexts({});
      setOverlappingReports(new Set());
      
      // Exit edit mode
      setIsEditMode(false);
      setEditingTitleId(null);
      setEditingTitle('');
      
      toast.success('Changes saved successfully!');
      
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes');
    }
  };

  // Cancel changes and revert to original state
  const handleCancelChanges = () => {
    // Revert dashboards to original state
    setDashboards(prev => prev.map(dashboard => {
      const originalTitle = originalState.dashboardTitles[dashboard.id];
      const originalLayout = originalState.layouts[dashboard.id];
      
      return {
        ...dashboard,
        name: originalTitle || dashboard.name,
        reports: dashboard.reports?.map(report => {
          const originalText = originalState.reportTitles[report.id];
          const originalLayoutItem = originalLayout?.find(item => item.i === report.id);
          
          return {
            ...report,
            data: originalText ? { ...report.data, text: originalText } : report.data,
            layout: originalLayoutItem ? {
              x: originalLayoutItem.x,
              y: originalLayoutItem.y,
              w: originalLayoutItem.w,
              h: originalLayoutItem.h
            } : report.layout
          };
        })
      };
    }));
    
    // Clear all pending changes
    setPendingDashboardTitles({});
    setPendingReportTitles({});
    setPendingLayouts({});
    setReportTitleTexts({});
    setOverlappingReports(new Set());
    
    // Exit edit mode
    setIsEditMode(false);
    setEditingTitleId(null);
    setEditingTitle('');
    
    toast.success('Changes cancelled');
  };

  // Collision detection function
  const detectCollisions = useCallback((layout: any[], dashboardId: string) => {
    const overlapping = new Set<string>();
    
    // Check each report against all others
    for (let i = 0; i < layout.length; i++) {
      const reportA = layout[i];
      
      for (let j = i + 1; j < layout.length; j++) {
        const reportB = layout[j];
        
        // Check if reports overlap
        const aLeft = reportA.x;
        const aRight = reportA.x + reportA.w;
        const aTop = reportA.y;
        const aBottom = reportA.y + reportA.h;
        
        const bLeft = reportB.x;
        const bRight = reportB.x + reportB.w;
        const bTop = reportB.y;
        const bBottom = reportB.y + reportB.h;
        
        // Check for overlap (not just touching)
        const horizontalOverlap = aLeft < bRight && aRight > bLeft;
        const verticalOverlap = aTop < bBottom && aBottom > bTop;
        
        if (horizontalOverlap && verticalOverlap) {
          overlapping.add(reportA.i);
          overlapping.add(reportB.i);
        }
      }
    }
    
    setOverlappingReports(overlapping);
    return overlapping;
  }, []);

  // Smart responsive sizing functions
  const getResponsiveSize = (layout: { w: number; h: number }) => {
    const { w, h } = layout;
    const area = w * h;
    
    // Calculate actual pixel dimensions using full screen width for mobile
    const actualWidth = currentViewType === 'mobile' 
      ? (w / 20) * (mobileScreenWidth - 8)  // Mobile: full screen width minus minimal padding
      : (w / 100) * 1200; // Desktop: 1200px total width
    
    const actualHeight = currentViewType === 'mobile' 
      ? h * 8  // Mobile: 8px per row (consistent with desktop)
      : h * 8; // Desktop: 8px per row
    
    return {
      width: actualWidth,
      height: actualHeight,
      area: actualWidth * actualHeight
    };
  };

  const getContentFontSize = (layout: { w: number; h: number }, contentLength: number = 10) => {
    const { width, height } = getResponsiveSize(layout);
    
    // Base size calculation on widget area and content length
    let baseSize = Math.sqrt(width * height) / 10; // Start with area-based sizing
    
    // Adjust for content length - longer content needs smaller font
    if (contentLength > 20) baseSize *= 0.8;
    else if (contentLength > 10) baseSize *= 0.9;
    
    // Apply view-specific constraints
    if (currentViewType === 'mobile') {
      baseSize = Math.max(8, Math.min(baseSize, 24)); // 8px to 24px range
    } else {
      baseSize = Math.max(10, Math.min(baseSize, 36)); // 10px to 36px range
    }
    
    // Convert to Tailwind classes
    if (baseSize <= 10) return { class: 'text-xs', size: 10 };
    if (baseSize <= 12) return { class: 'text-sm', size: 12 };
    if (baseSize <= 14) return { class: 'text-base', size: 14 };
    if (baseSize <= 16) return { class: 'text-lg', size: 16 };
    if (baseSize <= 18) return { class: 'text-xl', size: 18 };
    if (baseSize <= 20) return { class: 'text-2xl', size: 20 };
    if (baseSize <= 24) return { class: 'text-3xl', size: 24 };
    return { class: 'text-4xl', size: 28 };
  };

  const getTitleFontSize = (layout: { w: number; h: number }) => {
    const { width, height } = getResponsiveSize(layout);
    let titleSize = Math.sqrt(width * height) / 8; // Slightly larger than content
    
    if (currentViewType === 'mobile') {
      titleSize = Math.max(8, Math.min(titleSize, 16));
    } else {
      titleSize = Math.max(10, Math.min(titleSize, 20));
    }
    
    if (titleSize <= 8) return { class: 'text-xs', size: 8 };
    if (titleSize <= 10) return { class: 'text-xs', size: 10 };
    if (titleSize <= 12) return { class: 'text-sm', size: 12 };
    if (titleSize <= 14) return { class: 'text-sm', size: 14 };
    if (titleSize <= 16) return { class: 'text-base', size: 16 };
    return { class: 'text-lg', size: 18 };
  };

  const getIconSize = (layout: { w: number; h: number }) => {
    const { width, height } = getResponsiveSize(layout);
    let iconSize = Math.min(width, height) / 8; // Icon size based on smaller dimension
    
    if (currentViewType === 'mobile') {
      iconSize = Math.max(12, Math.min(iconSize, 24));
    } else {
      iconSize = Math.max(16, Math.min(iconSize, 32));
    }
    
    if (iconSize <= 12) return { class: 'w-3 h-3', size: 12 };
    if (iconSize <= 16) return { class: 'w-4 h-4', size: 16 };
    if (iconSize <= 20) return { class: 'w-5 h-5', size: 20 };
    if (iconSize <= 24) return { class: 'w-6 h-6', size: 24 };
    if (iconSize <= 28) return { class: 'w-7 h-7', size: 28 };
    return { class: 'w-8 h-8', size: 32 };
  };

  const getResponsivePadding = (layout: { w: number; h: number }) => {
    const { width, height } = getResponsiveSize(layout);
    const minDimension = Math.min(width, height);
    
    if (minDimension < 80) return 'p-1';      // Very small widgets
    if (minDimension < 120) return 'p-2';     // Small widgets  
    if (minDimension < 200) return 'p-3';     // Medium widgets
    return 'p-4';                             // Large widgets
  };

  const getResponsiveSpacing = (layout: { w: number; h: number }) => {
    const { width, height } = getResponsiveSize(layout);
    const minDimension = Math.min(width, height);
    
    if (minDimension < 80) return 'space-x-1 space-y-1';
    if (minDimension < 120) return 'space-x-2 space-y-1';
    if (minDimension < 200) return 'space-x-2 space-y-2';
    return 'space-x-3 space-y-2';
  };

  const handleDeleteDashboard = async (dashboardId: string) => {
    try {
      await deleteKPIDashboard(dashboardId);
    setDashboards(dashboards.filter(d => d.id !== dashboardId));
      toast.success('Dashboard deleted successfully');
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      toast.error('Failed to delete dashboard');
    }
  };

  const handleDeleteReport = async (dashboardId: string, reportId: string) => {
    try {
      await deleteDashboardReport(reportId);
      
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            reports: dashboard.reports?.filter(report => report.id !== reportId)
          };
        }
        return dashboard;
      }));
      toast.success('Report removed from dashboard');
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  const handleConfigureOppositeView = async (dashboardId: string, targetViewType: 'desktop' | 'mobile') => {
    try {
      setConfiguringView(true);
      
      const result = await configureOppositeView(dashboardId, targetViewType);
      
      // Update dashboard state with new configuration status
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            desktopConfigured: result.dashboard.desktop_configured,
            mobileConfigured: result.dashboard.mobile_configured
          };
        }
        return dashboard;
      }));
      
      // Reload the dashboard data to show the new reports
      await loadDashboards();
      
      toast.success(`${targetViewType === 'desktop' ? 'Desktop' : 'Mobile'} view configured successfully!`);
    } catch (error) {
      console.error('Error configuring opposite view:', error);
      toast.error(`Failed to configure ${targetViewType} view`);
    } finally {
      setConfiguringView(false);
    }
  };

  // Memoized mobile screen width for performance
  const mobileScreenWidth = useMemo(() => {
    return typeof window !== 'undefined' ? window.innerWidth : 390; // Default to iPhone width
  }, [screenWidth]);

  // Dynamic responsive sizing function for maximum font size - memoized for performance
  const getDynamicFontSize = useCallback((layout: { w: number; h: number }, textContent: string = '', isTitle: boolean = false) => {
    // Calculate actual pixel dimensions - use full screen width for mobile
    const actualWidth = currentViewType === 'mobile' 
      ? (layout.w / 20) * (mobileScreenWidth - 8)  // Mobile: full screen width minus minimal padding (4px each side)
      : (layout.w / 100) * 1200; // Desktop: 1200px total width
    
    const actualHeight = currentViewType === 'mobile' 
      ? layout.h * 8  // Mobile: 8px per row (consistent with desktop)
      : layout.h * 8; // Desktop: 8px per row
    
    // More aggressive space utilization to fill the card better
    const padding = currentViewType === 'mobile' ? 12 : 16; // Reduced padding
    const availableWidth = actualWidth - (padding * 2); // Left and right padding
    const availableHeight = actualHeight - (isTitle ? 15 : 25); // Less reserved space
    
    // More aggressive character width estimation for better space usage
    const avgCharWidth = isTitle ? 0.5 : 0.55; // Tighter estimation for more space usage
    const textLength = Math.max(textContent.length, 3); // Minimum length for calculation
    
    // Calculate font size based on available width (primary constraint)
    const widthBasedSize = (availableWidth * 0.95) / (textLength * avgCharWidth); // Use 95% of width
    
    // Calculate font size based on available height (secondary constraint)
    const heightBasedSize = availableHeight * 0.85; // Use 85% of available height
    
    // Use the smaller of the two to ensure it fits
    let optimalSize = Math.min(widthBasedSize, heightBasedSize);
    
    // Apply more generous constraints to better fill the space
    if (isTitle) {
      optimalSize = Math.max(12, Math.min(optimalSize, currentViewType === 'mobile' ? 32 : 42)); // Larger title sizes
    } else {
      optimalSize = Math.max(16, Math.min(optimalSize, currentViewType === 'mobile' ? 48 : 64)); // Much larger data sizes
    }
    
    // Add fallback for very long text - reduce size further if needed
    if (textContent.length > 15) {
      optimalSize *= 0.8; // Reduce by 20% for long text
    }
    if (textContent.length > 25) {
      optimalSize *= 0.8; // Reduce by another 20% for very long text
    }
    
    // Ensure minimum readable size
    optimalSize = Math.max(optimalSize, 10);
    
    return {
      fontSize: `${optimalSize}px`,
      lineHeight: '1.1',  // Slightly more line height for better readability
      fontWeight: isTitle ? '600' : '700',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    };
  }, [currentViewType, mobileScreenWidth]);

  // Dynamic font size for labels (much smaller relative to data) - memoized for performance
  const getDynamicLabelSize = useCallback((layout: { w: number; h: number }) => {
    const actualWidth = currentViewType === 'mobile' 
      ? (layout.w / 20) * (mobileScreenWidth - 8)  // Mobile: full screen width minus minimal padding
      : (layout.w / 100) * 1200; // Desktop: 1200px total width
    
    // Label size is much smaller but still proportional - more generous calculation for mobile
    const labelSize = currentViewType === 'mobile' 
      ? Math.max(10, Math.min(actualWidth / 25, 16)) // Mobile: larger min and max, better ratio
      : Math.max(9, Math.min(actualWidth / 30, 14));  // Desktop: original calculation
    
    return {
      fontSize: `${labelSize}px`,
      lineHeight: '1.1',
      fontWeight: '400', // Even lighter for labels
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    };
  }, [currentViewType, mobileScreenWidth]);

  // Dynamic font size for report titles (much more dynamic and prominent) - memoized for performance
  const getDynamicTitleSize = useCallback((layout: { w: number; h: number }) => {
    const actualWidth = currentViewType === 'mobile' 
      ? (layout.w / 20) * (mobileScreenWidth - 8)  // Mobile: full screen width minus minimal padding
      : (layout.w / 100) * 1200; // Desktop: 1200px total width
    
    const actualHeight = currentViewType === 'mobile' 
      ? layout.h * 8  // Mobile: 8px per row (consistent with desktop)
      : layout.h * 8; // Desktop: 8px per row
    
    // Title size should be proportional to both width and height but not too large
    // Base it on the smaller dimension to ensure it fits
    const baseDimension = Math.min(actualWidth, actualHeight);
    
    // Title takes roughly 15-25% of the smaller dimension
    let titleSize = baseDimension * 0.12; // Start with 12% of smaller dimension
    
    // Add width-based scaling for longer containers
    titleSize += actualWidth * 0.02; // Add 2% of width
    
    // Apply reasonable constraints based on view type - more generous for mobile
    if (currentViewType === 'mobile') {
      titleSize = Math.max(10, Math.min(titleSize, 24)); // 10px to 24px on mobile (increased max)
    } else {
      titleSize = Math.max(12, Math.min(titleSize, 28)); // 12px to 28px on desktop
    }
    
    return {
      fontSize: `${titleSize}px`,
      lineHeight: '1.2',
      fontWeight: '600',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    };
  }, [currentViewType, mobileScreenWidth]);

  // Dynamic sizing for comparison indicators (percentage changes) - memoized for performance
  const getDynamicComparisonSize = useCallback((layout: { w: number; h: number }) => {
    const actualWidth = currentViewType === 'mobile' 
      ? (layout.w / 20) * (mobileScreenWidth - 8)  // Mobile: full screen width minus minimal padding
      : (layout.w / 100) * 1200; // Desktop: 1200px total width
    
    // Comparison size should be smaller than main data but larger than labels
    // Base the size on container width with reasonable constraints
    const fontSize = currentViewType === 'mobile' 
      ? Math.max(8, Math.min(actualWidth / 35, 12)) // Mobile: 8px to 12px
      : Math.max(10, Math.min(actualWidth / 40, 14)); // Desktop: 10px to 14px
    
    // Icon size should be proportional to font size
    const iconSize = Math.max(fontSize * 0.8, 8); // Icon slightly smaller than text, minimum 8px
    
    return {
      fontSize: `${fontSize}px`,
      iconSize: `${iconSize}px`,
      lineHeight: '1.2',
      fontWeight: '500',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    };
  }, [currentViewType, mobileScreenWidth]);

  // Show loading state with skeleton
  if (loading) {
    return <KPIDashboardSkeleton currentViewType={currentViewType} />;
  }

  return (
    <div className={`min-h-screen ${isEditMode ? 'bg-gradient-to-br from-blue-50/50 via-white to-indigo-50/50' : 'bg-gray-50'}`} data-kpi-dashboard-page style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      {/* Main Page Header - only show if no dashboards for current view and not on mobile */}
      {dashboards.filter(dashboard => {
        return currentViewType === 'desktop' ? dashboard.desktopConfigured : dashboard.mobileConfigured;
      }).length === 0 && !loading && currentViewType === 'desktop' && (
        <div className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-2xl shadow-lg px-8 py-6 mb-8"
             style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-bold text-gray-900 text-3xl">KPI Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={isEditMode ? handleSaveChanges : toggleEditMode}
                className={`px-6 py-3 text-base font-semibold rounded-xl flex items-center space-x-2 transition-all duration-200 active:scale-95 shadow-sm ${
                  isEditMode 
                    ? 'text-white bg-blue-600 hover:bg-blue-700' 
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {!isEditMode && <Edit3 className="h-5 w-5" />}
                <span>{isEditMode ? 'Save' : 'Edit'}</span>
              </button>
              {isEditMode && (
                <>
                  <button 
                    onClick={handleCancelChanges}
                    className="px-6 py-3 text-base font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center space-x-2 transition-all duration-200 active:scale-95 shadow-sm"
                  >
                    <span>Cancel</span>
                  </button>
                  <button 
                    onClick={() => toast('Please create a dashboard first, then click "Add Reports" on that dashboard')}
                    className="px-6 py-3 text-base font-semibold text-white bg-blue-600 border border-blue-600 rounded-xl hover:bg-blue-700 flex items-center space-x-2 transition-all duration-200 active:scale-95 shadow-sm"
                    title={`Add reports to ${currentViewType} view only`}
                  >
                  <FileBarChart className="h-5 w-5" />
                  <span>Add Reports</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}


      <div className={`space-y-8 ${currentViewType === 'mobile' ? '' : ''}`}>
        {/* Render each dashboard - only show if configured for current view */}
        {dashboards.filter(dashboard => {
          // Only show dashboards that are configured for the current view type
          return currentViewType === 'desktop' ? dashboard.desktopConfigured : dashboard.mobileConfigured;
        }).map((dashboard) => (
          <div key={dashboard.id} className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-2xl shadow-lg"
               style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            {/* Dashboard Header */}
            <div className={`border-b border-white/20 ${currentViewType === 'mobile' ? 'px-2 py-1.5' : 'px-4 py-2'}`}>
                              <div className="flex items-center justify-between">
                <div className={`flex items-center ${currentViewType === 'mobile' ? 'flex-1 min-w-0' : ''}`}>
                  <div className="flex items-center space-x-3">
                    {editingTitleId === dashboard.id ? (
                      <input
                        ref={titleInputRef}
                        type="text"
                        value={editingTitle}
                        onChange={(e) => handleTitleChange(dashboard.id, e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Escape') {
                            handleCancelTitleEdit();
                          }
                        }}
                        className={`${currentViewType === 'mobile' ? 'text-lg max-w-[200px]' : 'text-xl'} font-bold text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                        autoFocus
                      />
                    ) : (
                      <div className="flex items-center space-x-2">
                        <h2 
                          className={`${currentViewType === 'mobile' ? 'text-lg truncate max-w-[200px]' : 'text-xl'} font-bold text-gray-900 ${
                            isEditMode ? 'cursor-pointer hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-all' : ''
                          }`}
                          onClick={() => {
                            if (isEditMode) {
                              handleStartTitleEdit(dashboard.id, pendingDashboardTitles[dashboard.id] || dashboard.name);
                            }
                          }}
                          title={isEditMode ? "Click to edit dashboard name" : ""}
                        >
                          {pendingDashboardTitles[dashboard.id] || dashboard.name}
                        </h2>
                      </div>
                    )}
                    
                  </div>
                </div>
                <div className={`flex items-center ${currentViewType === 'mobile' ? 'flex-wrap gap-1 ml-2' : 'space-x-3'}`}>
                  <DateRangeSelector
                    value={dashboard.dateRange || 'this_month'}
                    onChange={(newDateRange) => handleDateRangeChange(dashboard.id, newDateRange)}
                    defaultValue={dashboard.defaultDateRange}
                    onSetDefault={(newDefaultDateRange) => handleSetDefaultDateRange(dashboard.id, newDefaultDateRange)}
                  />
                  

                  
                  <button
                    onClick={isEditMode ? handleSaveChanges : toggleEditMode}
                    className={`${currentViewType === 'mobile' ? 'px-2 py-1.5 text-xs min-h-[36px]' : 'px-4 py-2 text-sm'} font-medium rounded-lg flex items-center ${currentViewType === 'mobile' ? 'space-x-1' : 'space-x-1'} transition-colors ${
                      isEditMode 
                        ? 'text-white bg-blue-600 hover:bg-blue-700' 
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {!isEditMode && <Edit3 className={currentViewType === 'mobile' ? 'h-3 w-3' : 'h-4 w-4'} />}
                    <span className={currentViewType === 'mobile' ? 'hidden sm:inline' : ''}>{isEditMode ? 'Save' : 'Edit'}</span>
                  </button>
                  {isEditMode && (
                    <>
                      <button 
                        onClick={handleCancelChanges}
                        className={`${currentViewType === 'mobile' ? 'px-2 py-1.5 text-xs min-h-[36px]' : 'px-4 py-2 text-sm'} font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-1 transition-all duration-200`}
                      >
                        <span className={currentViewType === 'mobile' ? 'hidden sm:inline' : ''}>Cancel</span>
                      </button>
                      <button 
                        onClick={() => handleAddReportsClick(dashboard.id)}
                        className={`${currentViewType === 'mobile' ? 'px-2 py-1.5 text-xs min-h-[36px]' : 'px-4 py-2 text-sm'} font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 flex items-center space-x-1 transition-all duration-200`}
                        title={`Add reports to ${currentViewType} view only`}
                      >
                      <FileBarChart className={currentViewType === 'mobile' ? 'h-3 w-3' : 'h-4 w-4'} />
                      <span className={currentViewType === 'mobile' ? 'hidden sm:inline' : ''}>Add Reports</span>
                      </button>
                    </>
                  )}
                  {isEditMode && (
                  <button
                    onClick={() => handleDeleteDashboard(dashboard.id)}
                    className={`text-white bg-red-500 hover:bg-red-600 ${currentViewType === 'mobile' ? 'p-1.5 min-h-[36px] min-w-[36px]' : 'p-2'} rounded-lg flex items-center justify-center transition-all duration-200`}
                    title="Delete Dashboard"
                  >
                      <Trash2 className={currentViewType === 'mobile' ? 'h-4 w-4' : 'h-5 w-5'} />
                  </button>
                  )}
                </div>
              </div>
            </div>

            {/* Dashboard Canvas */}
            <div className={currentViewType === 'mobile' ? 'p-0' : 'p-0'}>
              <div
                className={`min-h-[500px] relative overflow-hidden rounded-xl bg-white ${
                  isEditMode 
                    ? 'border-2 border-dashed border-gray-300' 
                    : 'border border-gray-200'
                }`}
                style={isEditMode ? {
                  backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                } : {}}
              >
                {/* Dashboard content rendering logic */}
                {(() => {
                  // Check if current view is configured
                  const isCurrentViewConfigured = currentViewType === 'desktop' 
                    ? dashboard.desktopConfigured 
                    : dashboard.mobileConfigured;
                  
                  // If current view is not configured, show empty state
                  if (!isCurrentViewConfigured) {
                    return (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <div className="mb-4">
                          {currentViewType === 'desktop' ? (
                            <Monitor className="h-16 w-16 mx-auto text-gray-300" />
                          ) : (
                            <Smartphone className="h-16 w-16 mx-auto text-gray-300" />
                          )}
                        </div>
                        <h3 className="text-lg font-medium mb-2">
                          {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} view not configured
                        </h3>
                        <p className="text-sm text-gray-400 mb-4">
                          This dashboard doesn't have a {currentViewType} layout configured yet.
                        </p>
                      </div>
                    );
                  }

                  return dashboard.reports && dashboard.reports.length > 0 ? (
                    /* Show Reports with Drag & Drop */
                    <div className={`${currentViewType === 'mobile' ? 'p-0' : 'p-0'}`} style={{ minHeight: '600px' }}>
                      {/* Smart canvas with responsive grid */}
                      <div 
                        style={{
                          minHeight: `${getGridHeight(dashboard)}px` // Smart canvas: Desktop 100x30 (240px), Mobile 20x40 (200px), expands with content
                        }}
                        className="w-full"
                      >
                        <GridLayout
                        className="layout"
                        data-edit-mode={isEditMode}
                        data-initial-load={!isInitialLoadComplete}
                        layout={dashboard.reports.map(report => ({
                          i: report.id,
                          x: report.layout.x,
                          y: report.layout.y,
                          w: report.layout.w,
                          h: report.layout.h
                        }))}
                        cols={currentViewType === 'mobile' ? 20 : 100}  // Fixed grid: Mobile = 20 cols, Desktop = 100 cols
                        rowHeight={currentViewType === 'mobile' ? 8 : 8}  // Consistent row height: 8px for both mobile and desktop for better scaling
                        onLayoutChange={(layout: any[]) => handleLayoutChange(layout, dashboard.id)}
                        isDraggable={isEditMode}
                        isResizable={isEditMode}
                        margin={[2, 2]}  // Smaller margins for finer positioning
                        containerPadding={[4, 4]}  // Reduced padding for more grid space
                        resizeHandles={['se', 'sw', 'ne', 'nw', 's', 'n', 'e', 'w']}  // All resize handles for maximum control
                        autoSize={true} // Let GridLayout auto-size vertically based on content
                        verticalCompact={false}
                        compactType={null}
                        useCSSTransforms={false}
                        preventCollision={false}  // Allow overlaps in edit mode
                        allowOverlap={true}  // Enable overlapping
                        width={currentViewType === 'mobile' ? mobileScreenWidth - 8 : 1200} // Mobile: cached screen width minus minimal padding, Desktop: 1200px for consistent scaling
                      >
                      {dashboard.reports.map((report) => (
                        <div key={report.id} className={`backdrop-blur-xl bg-white/70 border-2 rounded-2xl ${
                          getResponsivePadding(report.layout)
                        } transition-all duration-300 h-full flex flex-col relative overflow-hidden ${
                          isEditMode 
                            ? overlappingReports.has(report.id)
                              ? 'border-red-500 shadow-red-200 shadow-lg cursor-move'
                              : 'border-green-500 shadow-green-200 shadow-lg hover:border-green-400 cursor-move'
                            : 'border-gray-200/50 hover:scale-102'
                        }`}
                        style={{ 
                          backdropFilter: 'blur(20px)', 
                          WebkitBackdropFilter: 'blur(20px)',
                          boxShadow: '0 4px 15px -3px rgba(147, 51, 234, 0.03), 0 2px 6px -2px rgba(147, 51, 234, 0.02), 0 0 15px rgba(147, 51, 234, 0.03)',
                          transition: 'all 0.3s ease',
                          transform: 'none !important'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 8px 25px -5px rgba(147, 51, 234, 0.06), 0 4px 10px -3px rgba(147, 51, 234, 0.04), 0 0 25px rgba(147, 51, 234, 0.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 15px -3px rgba(147, 51, 234, 0.03), 0 2px 6px -2px rgba(147, 51, 234, 0.02), 0 0 15px rgba(147, 51, 234, 0.03)';
                        }}>
                          {isEditMode && (
                            <button
                              onClick={() => {
                                handleDeleteReport(dashboard.id, report.id);
                              }}
                              className="absolute top-2 right-2 text-gray-400 hover:text-red-600 p-1 rounded-full hover:bg-white/50 z-[9999] backdrop-blur-sm transition-all"
                              style={{ 
                                backdropFilter: 'blur(10px)', 
                                WebkitBackdropFilter: 'blur(10px)',
                                pointerEvents: 'all',
                                touchAction: 'none'
                              }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Title Element Rendering */}
                          {report.type === 'title' ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-center">
                              {isEditMode ? (
                                <input
                                  type="text"
                                  value={reportTitleTexts[report.id] ?? pendingReportTitles[report.id] ?? report.data?.text ?? 'Dashboard Title'}
                                  onChange={(e) => handleReportTitleChange(dashboard.id, report.id, e.target.value)}
                                  className="font-bold text-gray-900 bg-white/80 border border-white/30 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-center w-full backdrop-blur-sm transition-all"
                                  style={{
                                    ...getDynamicFontSize(report.layout, reportTitleTexts[report.id] ?? report.data?.text ?? 'Dashboard Title', true),
                                    backdropFilter: 'blur(10px)', 
                                    WebkitBackdropFilter: 'blur(10px)'
                                  }}
                                />
                              ) : (
                                <div 
                                  className="font-bold text-gray-900 transition-all duration-200 text-center w-full px-2 overflow-hidden"
                                  style={getDynamicFontSize(report.layout, pendingReportTitles[report.id] || report.data?.text || 'Dashboard Title', true)}
                                >
                                  <div className="line-clamp-2">
                                    {pendingReportTitles[report.id] || report.data?.text || 'Dashboard Title'}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : report.type === 'stock_alert' ? (
                            /* Stock Alert Custom Layout */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                                <div className="flex items-center">
                                  <span 
                                    className="text-gray-600 truncate"
                                    style={getDynamicTitleSize(report.layout)}
                                  >
                                    {report.title}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col items-center justify-center text-center">
                                <div 
                                  className="font-bold text-gray-900 mb-1"
                                  style={getDynamicFontSize(report.layout, String(report.data?.value || '0'))}
                                >
                                  {report.data?.value}
                                </div>
                                <div 
                                  className="text-gray-500 text-center leading-tight"
                                  style={getDynamicLabelSize(report.layout)}
                                >
                                  {report.data?.label}
                                </div>
                              </div>
                              {/* Supplier Orders List */}
                              <div className="flex-1 mt-2">
                                {supplierOrders.length === 0 ? (
                                  <div className="text-center py-4">
                                    <Package2 className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                                    <p className="text-xs text-gray-500">No suppliers need orders</p>
                                  </div>
                                ) : (
                                  <div className="max-h-40 overflow-y-auto space-y-2">
                                    {supplierOrders.map((order) => (
                                      <div 
                                        key={order.supplier.id} 
                                        className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex-1 min-w-0">
                                            <h5 className="text-xs font-medium text-gray-900 truncate">
                                              {order.supplier.first_name} {order.supplier.last_name}
                                            </h5>
                                            <p className="text-xs text-gray-500">
                                              {order.itemCount} item{order.itemCount !== 1 ? 's' : ''} • ${order.totalCost.toFixed(2)}
                                            </p>
                                          </div>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePlaceOrder(order.supplier.id);
                                            }}
                                            className="ml-2 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                                          >
                                            <ShoppingCart className="h-3 w-3 inline-block mr-1" />
                                            Order
                                          </button>
                                        </div>
                                        <div className="space-y-1">
                                          {order.items.slice(0, 2).map((item) => (
                                            <div key={item.item.id} className="flex justify-between text-xs text-gray-600">
                                              <span className="truncate">{item.item.name}</span>
                                              <span>{item.quantityToOrder} {item.item.unit}</span>
                                            </div>
                                          ))}
                                          {order.items.length > 2 && (
                                            <div className="text-xs text-gray-500">
                                              +{order.items.length - 2} more item{order.items.length - 2 !== 1 ? 's' : ''}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'sales_graph' ? (
                            /* Sales Graph Custom Layout */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                                <div className="flex items-center">
                                  <span 
                                    className="text-gray-600 line-clamp-2 truncate"
                                    style={getDynamicTitleSize(report.layout)}
                                  >
                                    {report.title}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col">
                                <div className="text-center mb-2">
                                  <div 
                                    className="font-bold text-gray-900"
                                    style={getDynamicFontSize(report.layout, report.data?.value || '$0.00')}
                                  >
                                    {report.data?.value || '$0.00'}
                                  </div>
                                  <div 
                                    className="text-gray-500"
                                    style={getDynamicLabelSize(report.layout)}
                                  >
                                    {report.data?.label || 'Sales Trend'}
                                  </div>
                                </div>
                                
                                {/* Chart Container */}
                                <div className="flex-1 min-h-0 overflow-hidden">
                                  {report.data?.chartData && report.data?.isPieChart ? (
                                    <div className="h-full w-full">
                                      <Pie 
                                        data={report.data.chartData}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: true,
                                              position: 'bottom',
                                              labels: {
                                                boxWidth: 12,
                                                font: {
                                                  size: 11
                                                },
                                                padding: 10,
                                                usePointStyle: true
                                              }
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(147, 51, 234, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                label: (context: any) => {
                                                  const label = context.label || '';
                                                  const value = context.parsed;
                                                  return `${label}: ${value} booking${value !== 1 ? 's' : ''}`;
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  ) : report.data?.chartData && (
                                    <div className="h-full w-full">
                                      <Line 
                                        data={report.data.chartData}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: false
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(147, 51, 234, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                label: (context: any) => {
                                                  return formatCurrency(context.parsed.y);
                                                }
                                              }
                                            }
                                          },
                                          scales: {
                                            y: {
                                              beginAtZero: true,
                                              grid: {
                                                color: 'rgba(147, 51, 234, 0.1)'
                                              },
                                              ticks: {
                                                callback: (value: any) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
                                                font: {
                                                  size: 10
                                                }
                                              }
                                            },
                                            x: {
                                              grid: {
                                                display: false
                                              },
                                              ticks: {
                                                font: {
                                                  size: 10
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'online_booking_volume' ? (
                            /* Online Booking Volume Funnel Custom Layout */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center mb-2 flex-shrink-0">
                                <div className="flex items-center">
                                  <span 
                                    className="text-gray-600 line-clamp-2 truncate"
                                    style={getDynamicTitleSize(report.layout)}
                                  >
                                    {report.title}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col">
                                <div className="text-center mb-2">
                                  <div 
                                    className="font-bold text-gray-900"
                                    style={getDynamicFontSize(report.layout, report.data?.value || '0.0%')}
                                  >
                                    {report.data?.value || '0.0%'}
                                  </div>
                                  <div 
                                    className="text-gray-500"
                                    style={getDynamicLabelSize(report.layout)}
                                  >
                                    {report.data?.label || 'Conversion Rate'}
                                  </div>
                                </div>
                                
                                {/* Funnel Steps - maintain existing visualization but scale container */}
                                <div className="flex-1 min-h-0 overflow-hidden">
                                  <div className="space-y-2">
                                    {(report.data?.funnelData || []).map((step: any, index: number) => {
                                      const maxCount = Math.max(...(report.data?.funnelData || []).map((s: any) => s.count));
                                      const percentage = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
                                      
                                      return (
                                        <div key={step.step} className="flex items-center space-x-3">
                                          <div className="flex-1 bg-gray-100/50 backdrop-blur-sm rounded-xl h-4 relative overflow-hidden border border-white/30"
                                               style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                                            <div 
                                              className={`h-full rounded-xl transition-all duration-300 ${
                                                index === 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                                                index === 1 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                                                index === 2 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                                                index === 3 ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                                                index === 4 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                                                'bg-gradient-to-r from-blue-500 to-blue-600'
                                              }`}
                                              style={{ width: `${percentage}%` }}
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center">
                                              <span className="text-xs font-medium text-gray-700">
                                                {step.count}
                                              </span>
                                            </div>
                                          </div>
                                          <div className="text-xs text-gray-600 min-w-0 flex-shrink-0 w-16 truncate">
                                            {step.step}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                
                                {/* Banner Submissions at the bottom */}
                                {report.data?.showBannerSubmits && report.data?.bannerSubmits > 0 && (
                                  <div className="mt-3 pt-2 border-t border-gray-200">
                                    <div className="flex items-center justify-center space-x-2 bg-blue-50 px-3 py-2 rounded-lg">
                                      <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" clipRule="evenodd" />
                                      </svg>
                                      <span className="text-sm font-medium text-blue-700">
                                        Banner Submissions: {report.data.bannerSubmits}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'booking_source' && report.data?.isPieChart ? (
                            /* Booking Source Pie Chart Layout - Horizontal */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center mb-2 flex-shrink-0">
                                <span 
                                  className="text-gray-600 line-clamp-2 truncate"
                                  style={getDynamicTitleSize(report.layout)}
                                >
                                  {report.title}
                                </span>
                              </div>
                              
                              {/* Horizontal Layout: Left sidebar + Right chart */}
                              <div className="flex-1 flex flex-row min-h-0">
                                {/* Left Sidebar: Legend Only */}
                                <div className="flex flex-col w-1/3 pr-2 flex-shrink-0">
                                  {/* Custom Legend on Left */}
                                  <div className="flex-1 overflow-y-auto">
                                    {report.data?.chartData?.labels?.map((label: string, index: number) => {
                                      const backgroundColor = report.data.chartData.datasets[0].backgroundColor[index];
                                      const dataValue = report.data.chartData.datasets[0].data[index];
                                      return (
                                        <div key={index} className="flex items-center mb-1 text-xs">
                                          <div 
                                            className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                                            style={{ backgroundColor }}
                                          ></div>
                                          <div className="flex-1 min-w-0">
                                            <div className="truncate text-gray-700">
                                              {label.split(' (')[0]}
                                            </div>
                                            <div className="text-gray-500">
                                              {dataValue} booking{dataValue !== 1 ? 's' : ''}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                
                                {/* Right Side: Pie Chart */}
                                <div className="flex-1 min-h-0 overflow-hidden">
                                  {report.data?.chartData && (
                                    <div className="h-full w-full">
                                      <Pie 
                                        data={report.data.chartData}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: false // Hide default legend since we have custom one
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(147, 51, 234, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                label: (context: any) => {
                                                  const label = context.label || '';
                                                  const value = context.parsed;
                                                  return `${label}: ${value} booking${value !== 1 ? 's' : ''}`;
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* Regular Data Report Rendering */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center mb-1 flex-shrink-0">
                                <span 
                                  className="text-gray-600 line-clamp-2 truncate"
                                  style={getDynamicTitleSize(report.layout)}
                                >
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 flex flex-col items-start justify-center text-left">
                                <div className="flex items-center space-x-2 mb-0.5 w-full">
                                  {/* Special layout for Team Rating - stars first, then number */}
                                  {report.type === 'team_rating' && report.data?.value && report.data.value !== '0.0' ? (
                                    <div className="flex items-center space-x-1.5">
                                      <StarRating 
                                        rating={parseFloat(report.data.value)} 
                                        dynamicSize={getDynamicFontSize(report.layout, String(report.data?.value || '')).fontSize ? 
                                          parseInt(getDynamicFontSize(report.layout, String(report.data?.value || '')).fontSize.replace('px', '')) : 
                                          16
                                        }
                                      />
                                      <div 
                                        className="font-bold text-gray-900"
                                        style={{
                                          ...getDynamicFontSize(report.layout, String(report.data?.value || '')),
                                          wordBreak: 'keep-all',
                                          overflowWrap: 'normal'
                                        }}
                                      >
                                        {report.data?.value}
                                      </div>
                                    </div>
                                  ) : (
                                    <div 
                                      className={`font-bold text-left ${
                                        report.type === 'missed_calls' ? 'text-red-600' : 'text-gray-900'
                                      }`}
                                      style={{
                                        ...getDynamicFontSize(report.layout, String(report.data?.value || '')),
                                        wordBreak: 'keep-all',
                                        overflowWrap: 'normal'
                                      }}
                                    >
                                      {report.data?.value}
                                    </div>
                                  )}
                                  
                                  {/* Comparison indicator on same line */}
                                  {report.data?.comparison && (() => {
                                    const comparisonStyles = getDynamicComparisonSize(report.layout);
                                    return (
                                      <div className="flex items-center">
                                        <div className="flex items-center space-x-1 px-2 py-1 rounded-full font-medium"
                                             style={{
                                               backgroundColor: (() => {
                                                 if (report.type === 'missed_calls') {
                                                   // For missed calls: up is bad (red), down is good (green)
                                                   return report.data.comparison.direction === 'up' 
                                                     ? 'rgba(239, 68, 68, 0.1)' 
                                                     : report.data.comparison.direction === 'down' 
                                                     ? 'rgba(34, 197, 94, 0.1)' 
                                                     : 'rgba(107, 114, 128, 0.1)';
                                                 } else {
                                                   // For normal metrics: up is good (green), down is bad (red)
                                                   return report.data.comparison.direction === 'up' 
                                                     ? 'rgba(34, 197, 94, 0.1)' 
                                                     : report.data.comparison.direction === 'down' 
                                                     ? 'rgba(239, 68, 68, 0.1)' 
                                                     : 'rgba(107, 114, 128, 0.1)';
                                                 }
                                               })(),
                                               color: (() => {
                                                 if (report.type === 'missed_calls') {
                                                   // For missed calls: up is bad (red), down is good (green)
                                                   return report.data.comparison.direction === 'up' 
                                                     ? '#dc2626' 
                                                     : report.data.comparison.direction === 'down' 
                                                     ? '#16a34a' 
                                                     : '#6b7280';
                                                 } else {
                                                   // For normal metrics: up is good (green), down is bad (red)
                                                   return report.data.comparison.direction === 'up' 
                                                     ? '#16a34a' 
                                                     : report.data.comparison.direction === 'down' 
                                                     ? '#dc2626' 
                                                     : '#6b7280';
                                                 }
                                               })(),
                                               fontSize: comparisonStyles.fontSize,
                                               fontWeight: comparisonStyles.fontWeight,
                                               fontFamily: comparisonStyles.fontFamily,
                                               lineHeight: comparisonStyles.lineHeight
                                             }}>
                                          {report.data.comparison.direction === 'up' && (
                                            <svg 
                                              style={{ width: comparisonStyles.iconSize, height: comparisonStyles.iconSize }} 
                                              fill="currentColor" 
                                              viewBox="0 0 20 20"
                                            >
                                              <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 4.414 6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                            </svg>
                                          )}
                                          {report.data.comparison.direction === 'down' && (
                                            <svg 
                                              style={{ width: comparisonStyles.iconSize, height: comparisonStyles.iconSize }} 
                                              fill="currentColor" 
                                              viewBox="0 0 20 20"
                                            >
                                              <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 15.586l3.293-3.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                          )}
                                          {report.data.comparison.direction === 'same' && (
                                            <svg 
                                              style={{ width: comparisonStyles.iconSize, height: comparisonStyles.iconSize }} 
                                              fill="currentColor" 
                                              viewBox="0 0 20 20"
                                            >
                                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                            </svg>
                                          )}
                                          <span>
                                            {Math.round(report.data.comparison.percentage)}%
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                                
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                        </GridLayout>
                      </div>
                    </div>
                    ) : (
                      /* Empty state for individual dashboard with smart canvas height */
                      <div 
                        className="flex flex-col items-center justify-center" 
                        style={{ minHeight: `${getGridHeight(dashboard)}px` }}
                      >
                        <Activity className="h-12 w-12 mb-4 text-gray-300" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {isEditMode ? 'Start Building Your Dashboard' : 'No Reports Added Yet'}
                        </h3>
                        <p className="text-center max-w-md text-gray-500 mb-6">
                          {isEditMode 
                            ? 'Click "Add Reports" to add widgets and reports to your dashboard.'
                            : 'Switch to Edit mode to add reports and customize this dashboard.'
                          }
                        </p>
                      </div>
                    );
                })()}
              </div>
            </div>
          </div>
        ))}

        {/* Create Dashboard Button */}
        <div className="flex flex-col items-center">
          {dashboards.filter(dashboard => {
            return currentViewType === 'desktop' ? dashboard.desktopConfigured : dashboard.mobileConfigured;
          }).length === 0 ? (
            // Initial state for current view type
            <>
              <div className="flex flex-col items-center justify-center min-h-[400px]">
                {/* Check if there are dashboards in the opposite view that can be configured */}
                {dashboards.some(dashboard => 
                  currentViewType === 'desktop' ? dashboard.mobileConfigured && !dashboard.desktopConfigured 
                  : dashboard.desktopConfigured && !dashboard.mobileConfigured
                ) ? (
                  <div className="text-center max-w-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      No {currentViewType} dashboards configured
                    </h3>
                    <p className="text-gray-600 text-sm mb-6">
                      You have dashboards configured for {currentViewType === 'desktop' ? 'mobile' : 'desktop'} view. 
                      Configure them for {currentViewType} view or create a new independent dashboard.
                    </p>
                    <div className="space-y-3">
                      <button 
                        onClick={() => setIsModalOpen(true)}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-xl hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 inline mr-2" />
                        Create New {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} Dashboard
                      </button>
                      <p className="text-xs text-gray-500">
                        Create a new dashboard optimized for {currentViewType} view
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-gray-600 text-sm mb-4">Create Your First {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} Dashboard</p>
                    <button 
                      onClick={() => setIsModalOpen(true)}
                      className="bg-white rounded-full shadow-lg border border-gray-200 p-4 hover:shadow-xl transition-shadow"
                    >
                      <Plus className="h-8 w-8 text-gray-400" />
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            // After dashboards exist - smaller button below
            <button 
              onClick={() => setIsModalOpen(true)}
              className="px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-xl hover:bg-blue-700 flex items-center space-x-2 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              <span>Add Another Dashboard</span>
            </button>
          )}
        </div>
      </div>

      {/* Create Dashboard Modal */}
      {isModalOpen && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4`} style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
          <div className="backdrop-blur-xl bg-white/90 rounded-2xl shadow-xl p-8 border border-white/20 w-full max-w-md mx-4"
               style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Create New Dashboard
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <label htmlFor="dashboard-name" className="block text-sm font-medium text-gray-700 mb-2">
                Dashboard Name
              </label>
              <input
                type="text"
                id="dashboard-name"
                value={dashboardName}
                onChange={(e) => setDashboardName(e.target.value)}
                placeholder="Enter dashboard name..."
                className="w-full px-4 py-3 backdrop-blur-sm bg-white/70 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                autoFocus
              />
            </div>
            
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-white/70 border border-white/30 rounded-xl hover:bg-white/90 transition-all duration-200 backdrop-blur-sm"
                style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateDashboard}
                disabled={!dashboardName.trim()}
                className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
              >
                Create Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Reports Modal */}
      {isAddReportsModalOpen && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4`} style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
          <div className="backdrop-blur-xl bg-white/90 rounded-2xl shadow-xl p-8 border border-white/20 w-full max-w-4xl mx-4 max-h-[80vh] overflow-y-auto"
               style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Add Reports to {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} View
              </h2>
              <button
                onClick={handleAddReportsModalClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Available Reports</h3>
              <p className="text-xs text-gray-500 mb-4">
                Reports added here will appear in the {currentViewType} view.
              </p>
              
              {/* Design Elements Section */}
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-3">Design Elements</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Title Element */}
                  <div 
                    onClick={() => handleReportClick('title', 'Title')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Title</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">Dashboard Title</div>
                      <div className="text-xs text-gray-500">Add custom text</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales & Revenue Section */}
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-3">Sales & Revenue</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Sales Report */}
                  <div 
                    onClick={() => handleReportClick('sales', 'Sales')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Sales</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$24,567</div>
                      <div className="text-xs text-gray-500">Total Sales After Discounts</div>
                    </div>
                  </div>

                  {/* Upsells Report */}
                  <div 
                    onClick={() => handleReportClick('upsells', 'Upsells')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Upsells</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$8,423</div>
                      <div className="text-xs text-gray-500">Additional Services Sold</div>
                    </div>
                  </div>

                  {/* Closed Sales Report */}
                  <div 
                    onClick={() => handleReportClick('closed_sales', 'Closed Sales')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Closed Sales</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$32,145</div>
                      <div className="text-xs text-gray-500">Completed Sales</div>
                    </div>
                  </div>

                  {/* Net Profit Report */}
                  <div 
                    onClick={() => handleReportClick('net_profit', 'Net Profit')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Net Profit</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$12,340</div>
                      <div className="text-xs text-gray-500">After All Expenses</div>
                    </div>
                  </div>

                  {/* Proposals Sent Report */}
                  <div 
                    onClick={() => handleReportClick('proposals_sent', 'Proposals Sent')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Proposals Sent</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">23</div>
                      <div className="text-xs text-gray-500">Total Proposals Sent</div>
                    </div>
                  </div>

                  {/* Proposals Booked Report */}
                  <div 
                    onClick={() => handleReportClick('proposals_booked', 'Proposals Booked')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Proposals Booked</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">8</div>
                      <div className="text-xs text-gray-500">Proposals Converted</div>
                    </div>
                  </div>

                  {/* Sales Graph Report */}
                  <div 
                    onClick={() => handleReportClick('sales_graph', 'Sales Graph')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Sales Graph</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$24,567</div>
                      <div className="text-xs text-gray-500">Sales Trend Chart</div>
                    </div>
                  </div>

                  {/* Average Sale Report */}
                  <div 
                    onClick={() => handleReportClick('average_sale', 'Average Sale')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Average Sale</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$456.78</div>
                      <div className="text-xs text-gray-500">Per Booking</div>
                    </div>
                  </div>

                  {/* Booking Source Report */}
                  <div 
                    onClick={() => handleReportClick('booking_source', 'Booking Source')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Booking Source</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">142</div>
                      <div className="text-xs text-gray-500">Total Bookings by Source</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Operations Section */}
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-3">Operations</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Total Jobs Report */}
                  <div 
                    onClick={() => handleReportClick('total_jobs', 'Total Jobs')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Total Jobs</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">247</div>
                      <div className="text-xs text-gray-500">All Bookings</div>
                    </div>
                  </div>

                  {/* Completed Jobs Report */}
                  <div 
                    onClick={() => handleReportClick('completed_jobs', 'Completed Jobs')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Completed Jobs</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">189</div>
                      <div className="text-xs text-gray-500">Finished Bookings</div>
                    </div>
                  </div>

                  {/* Labour Report */}
                  <div 
                    onClick={() => handleReportClick('labour', 'Labour')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Labour</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$9,560</div>
                      <div className="text-xs text-gray-500">Labour Costs</div>
                    </div>
                  </div>

                  {/* Job Materials Report */}
                  <div 
                    onClick={() => handleReportClick('job_materials', 'Job Materials')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Job Materials</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$5,240</div>
                      <div className="text-xs text-gray-500">Materials Used</div>
                    </div>
                  </div>

                  {/* Online Booking Volume Report */}
                  <div 
                    onClick={() => handleReportClick('online_booking_volume', 'Online Booking Volume')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Online Booking Volume</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">87</div>
                      <div className="text-xs text-gray-500">Online Bookings</div>
                    </div>
                  </div>

                  {/* Team Rating Report */}
                  <div 
                    onClick={() => handleReportClick('team_rating', 'Team Rating')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Team Rating</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">4.8</div>
                      <div className="text-xs text-gray-500">Customer Rating</div>
                    </div>
                  </div>

                  {/* Feedback % Report */}
                  <div 
                    onClick={() => handleReportClick('feedback_percentage', 'Feedback %')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Feedback %</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">78%</div>
                      <div className="text-xs text-gray-500">Response Rate</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Analytics Section */}
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-3">Analytics</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Website Visits Report */}
                  <div 
                    onClick={() => handleReportClick('website_visits', 'Website Visits')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Website Visits</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">1,247</div>
                      <div className="text-xs text-gray-500">Total Website Visits</div>
                    </div>
                  </div>

                  {/* Google Reviews Report */}
                  <div 
                    onClick={() => handleReportClick('google_reviews', 'Google Reviews')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Google Reviews</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">24</div>
                      <div className="text-xs text-gray-500">Review Clicks</div>
                    </div>
                  </div>

                  {/* Phone Calls Report */}
                  <div 
                    onClick={() => handleReportClick('phone_calls', 'Phone Calls')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Phone Calls</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">47</div>
                      <div className="text-xs text-gray-500">Incoming Phone Calls</div>
                    </div>
                  </div>

                  {/* Missed Calls Report */}
                  <div 
                    onClick={() => handleReportClick('missed_calls', 'Missed Calls')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Missed Calls</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">12</div>
                      <div className="text-xs text-gray-500">Missed Calls</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Inventory Section */}
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-3">Inventory</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Supplies Purchased Report */}
                  <div 
                    onClick={() => handleReportClick('supplies_purchased', 'Supplies Purchased')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Supplies Purchased</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">$3,892</div>
                      <div className="text-xs text-gray-500">Inventory Purchases</div>
                    </div>
                  </div>

                  {/* Stock Alert Report */}
                  <div 
                    onClick={() => handleReportClick('stock_alert', 'Stock Alert')}
                    className="backdrop-blur-sm bg-white/60 border border-white/30 rounded-2xl p-4 hover:bg-white/80 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Stock Alert</h4>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-900">3</div>
                      <div className="text-xs text-gray-500">Items Need Reorder</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end">
              <button
                onClick={handleAddReportsModalClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 active:scale-95 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile-specific optimizations */}
      {currentViewType === 'mobile' && (
        <style jsx global>{`
          /* Mobile touch optimizations */
          * {
            -webkit-tap-highlight-color: rgba(0,0,0,0);
            touch-action: manipulation;
          }

          /* Improve scroll performance on mobile */
          .layout {
            -webkit-overflow-scrolling: touch;
            -webkit-transform: translateZ(0);
            backface-visibility: hidden;
          }

          /* Better touch targets for mobile */
          @media (max-width: 768px) {
            /* Ensure all buttons meet minimum touch target size */
            button {
              min-height: 44px;
              min-width: 44px;
            }

            /* Optimize grid interactions for touch */
            .react-grid-item {
              touch-action: manipulation;
            }

            .react-resizable-handle {
              width: 20px !important;
              height: 20px !important;
            }

            /* Prevent text selection during drag operations */
            .react-grid-layout {
              -webkit-user-select: none;
              user-select: none;
            }

            /* Optimize report card interactions */
            .report-card {
              touch-action: manipulation;
              -webkit-tap-highlight-color: rgba(147, 51, 234, 0.1);
            }

            /* Better spacing for mobile interactions */
            .mobile-button-group {
              gap: 12px;
            }

            /* Optimize scrolling in dashboard canvas */
            .dashboard-canvas {
              -webkit-overflow-scrolling: touch;
              overscroll-behavior: contain;
            }
          }

          /* iOS-specific fixes */
          @supports (-webkit-touch-callout: none) {
            /* Prevent zoom on double tap */
            .dashboard-content {
              touch-action: manipulation;
            }

            /* Fix sticky hover states on iOS */
            @media (hover: none) {
              button:hover {
                background-color: initial;
              }
            }
          }

          /* Performance optimizations for mobile */
          @media (max-width: 768px) {
            .react-grid-layout {
              will-change: transform;
            }

            .react-grid-item {
              will-change: transform;
            }

            /* Reduce motion for better performance on lower-end devices */
            @media (prefers-reduced-motion: reduce) {
              * {
                animation-duration: 0.01ms !important;
                animation-iteration-count: 1 !important;
                transition-duration: 0.01ms !important;
              }
            }
          }

          /* Completely disable all initial animations for KPI reports */
          .react-grid-item {
            transition: none !important;
            transform: none !important;
            animation: none !important;
          }

          .react-grid-item.react-grid-placeholder {
            transition: none !important;
            animation: none !important;
          }

          /* Disable all grid layout animations initially */
          .react-grid-layout {
            transition: none !important;
            animation: none !important;
          }

          /* Ensure the grid container itself doesn't animate */
          .layout[data-initial-load="true"] {
            transition: none !important;
            animation: none !important;
          }

          /* Only enable transitions when in edit mode and after initial load */
          .layout[data-edit-mode="true"]:not([data-initial-load="true"]) .react-grid-item {
            transition: all 0.3s ease !important;
          }

          /* Prevent any transform animations on initial load */
          .react-grid-item > * {
            transition: none !important;
            animation: none !important;
          }

          /* Ensure grid items are positioned correctly from the start */
          .layout[data-initial-load="true"] .react-grid-item {
            transform: translate3d(0, 0, 0) !important;
            transition: none !important;
            animation: none !important;
          }
        `}</style>
      )}
    </div>
  );
}

// Wrap with PermissionGuard
function KPIDashboardWithPermission() {
  return (
    <PermissionGuard permission="kpiDashboard.pageAccess">
      <KPIDashboard />
    </PermissionGuard>
  );
}

export default KPIDashboardWithPermission; 

